import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useBroadcastConfig, useBroadcastHistory, useDeleteBroadcast, useClearBroadcastHistory, useBroadcastSchedules, useCreateSchedule, useUpdateSchedule, useDeleteSchedule, useToggleSchedule, useClearSchedules, useBroadcastCampaigns, useCreateCampaign, useUpdateCampaign, useDeleteCampaign, useToggleCampaign, useTemplates } from '@/api/hooks/useBroadcast'
import type { BroadcastCampaign } from '@/api/hooks/useBroadcast'
import { usePresets, useCreatePreset, useUpdatePreset, useDeletePreset } from '@/api/hooks/usePresets'
import type { BroadcastPreset } from '@/api/hooks/usePresets'
import { useGamPeakHours } from '@/api/hooks/useGam'
import { api, getAuthHeaders } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog'
import { toast } from 'sonner'
import {
  Send, Users, Clock, ImagePlus, Radio, Megaphone, CheckCircle, XCircle, AlertTriangle, Shuffle,
  X, Loader2, Link, History, Trash2, ChevronDown, ChevronUp, ChevronLeft,
  ChevronRight, Play, Shield, Plus, Repeat, CalendarClock, Layers, RefreshCw,
  Search, Pencil, Copy, TrendingUp, Zap, EyeOff,
} from 'lucide-react'
import { useLanguage } from '@/i18n/LanguageContext'
import { Switch } from '@/components/ui/switch'
import { useAuth } from '@/api/hooks/useAuth'
import { cn } from '@/lib/utils'
import { PermissionBadge } from '@/components/shared/PermissionBadge'
import { TemplatesTab } from './TemplatesTab'

/* ========================================================================
   TYPES
   ======================================================================== */

interface BroadcastProgressData {
  status: string; processed: number; total_contacts: number
  sent_count: number; failed_count: number; skipped_count: number; tagged_count: number
  account?: string
  wave?: number; totalWaves?: number; waveStartsAt?: string
  rateLimitInfo?: { hits: number; wait_seconds: number; resumes_at: string; error_code: number } | null
}

/* ========================================================================
   BROADCAST PANEL — polls active broadcasts and renders live progress
   ======================================================================== */

function BroadcastPanel({ onAllComplete, t }: {
  broadcastIds?: string[]
  onAllComplete: () => void
  onProgress?: (id: string, data: BroadcastProgressData | null) => void
  t: (s: string) => string
}) {
  const [summary, setSummary] = useState<{
    totalPages: number; completedPages: number; totalSent: number;
    totalFailed: number; totalProcessed: number; totalContacts: number; rate: number;
    pages: Array<{ id: string; account: string; status: string; sent: number; failed: number; processed: number; total: number; wave?: number; totalWaves?: number; failReason?: string; failMessage?: string; profiles?: string[]; reconnectUrl?: string }>
  } | null>(null)
  const [cancelling, setCancelling] = useState(false)

  const handleCancel = async () => {
    if (!confirm(t('Parar este broadcast agora? As paginas que ainda nao enviaram serao canceladas.'))) return
    setCancelling(true)
    try {
      await api.post('/broadcast/cancel-active', {})
      setSummary(null)
      onAllComplete()
    } catch {
      alert(t('Erro ao parar o broadcast'))
    } finally {
      setCancelling(false)
    }
  }

  useEffect(() => {
    let active = true
    const poll = async () => {
      try {
        const res = await api.get('/broadcast/active-summary')
        if (!active) return
        setSummary(res)
        if (res && res.completedPages === res.totalPages) {
          setTimeout(onAllComplete, 5000)
        }
      } catch { /* ignore */ }
      if (active) setTimeout(poll, 3000)
    }
    poll()
    return () => { active = false }
  }, [])

  if (!summary) return null

  const totalPages = summary.totalPages || 0
  const completedPages = summary.completedPages || 0
  const totalSent = summary.totalSent || 0
  const totalFailed = summary.totalFailed || 0
  const totalProcessed = summary.totalProcessed || 0
  const totalContacts = summary.totalContacts || 0
  const rate = summary.rate || 0
  const pages = summary.pages || []
  const isAllDone = completedPages === totalPages
  const pct = totalContacts > 0 ? Math.min(100, Math.round((totalProcessed / totalContacts) * 100)) : 0

  return (
    <div className={`rounded-2xl border p-5 space-y-4 backdrop-blur-sm transition-all duration-300 ${isAllDone ? 'border-emerald-500/15 bg-card/50' : 'border-primary/15 bg-card/50'}`}>
      {/* Title bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className={`flex h-7 w-7 items-center justify-center rounded-lg ${isAllDone ? 'bg-emerald-500/10' : 'bg-primary/10'}`}>
            {isAllDone ? <CheckCircle className="h-3.5 w-3.5 text-emerald-500" /> : <Megaphone className="h-3.5 w-3.5 text-primary" />}
          </div>
          <span className="text-sm font-bold">{isAllDone ? t('Broadcast Concluido') : t('Broadcast Ativo')}</span>
          <span className={`text-[10px] px-2 py-0.5 rounded-md font-semibold ${isAllDone ? 'bg-emerald-500/10 text-emerald-500' : 'bg-primary/10 text-primary'}`}>
            {totalPages} {t('paginas')}
          </span>
        </div>
        {!isAllDone && (
          <div className="flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
            <button
              type="button"
              onClick={handleCancel}
              disabled={cancelling}
              className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors disabled:opacity-50"
            >
              {cancelling ? '...' : <><X className="h-3 w-3" /> {t('Parar')}</>}
            </button>
          </div>
        )}
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex justify-between items-center mb-1.5">
          <span className="text-[11px] text-muted-foreground">{isAllDone ? t('Concluido') : t('Progresso geral')}</span>
          <span className="text-xs font-bold tabular-nums">{totalSent.toLocaleString()} {t('enviados')} · {completedPages}/{totalPages} {t('paginas')}</span>
        </div>
        <div className="w-full h-2 bg-muted/30 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700 ease-out"
            style={{
              width: `${pct}%`,
              background: isAllDone ? 'linear-gradient(90deg, #10b981, #059669)' : 'linear-gradient(90deg, var(--color-primary), color-mix(in srgb, var(--color-primary) 70%, #fff))',
            }}
          />
        </div>
        {!isAllDone && <div className="text-[10px] text-center text-muted-foreground/50 mt-1 tabular-nums">{pct}% {t('concluido')}</div>}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-2">
        <div className="flex items-center gap-2 rounded-xl bg-emerald-500/8 p-2.5">
          <CheckCircle className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
          <div>
            <div className="text-sm font-extrabold tabular-nums">{totalSent.toLocaleString()}</div>
            <div className="text-[9px] text-muted-foreground/60">{t('Entregues')}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-xl bg-amber-500/8 p-2.5">
          <Users className="h-3.5 w-3.5 text-amber-500 shrink-0" />
          <div>
            <div className="text-sm font-extrabold tabular-nums">{totalFailed.toLocaleString()}</div>
            <div className="text-[9px] text-muted-foreground/60">{t('Indisponiveis')}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-xl bg-emerald-500/8 p-2.5">
          <Send className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
          <div>
            <div className="text-sm font-extrabold tabular-nums">{rate}%</div>
            <div className="text-[9px] text-muted-foreground/60">{t('Taxa entrega')}</div>
          </div>
        </div>
        <div className={`flex items-center gap-2 rounded-xl p-2.5 ${isAllDone ? 'bg-emerald-500/8' : 'bg-primary/8'}`}>
          {isAllDone ? <CheckCircle className="h-3.5 w-3.5 text-emerald-500 shrink-0" /> : <Clock className="h-3.5 w-3.5 text-primary shrink-0" />}
          <div>
            <div className="text-sm font-extrabold tabular-nums">{isAllDone ? t('Pronto') : `${totalPages - completedPages} ${t('paginas')}`}</div>
            <div className="text-[9px] text-muted-foreground/60">{isAllDone ? t('Concluido') : t('Restantes')}</div>
          </div>
        </div>
      </div>

      {/* Per-page chips */}
      {pages.length > 0 && (() => {
        const maxWaves = Math.max(...pages.map(d => d.totalWaves || 1))
        const hasWaves = maxWaves > 1
        const waves: Record<number, typeof pages> = {}
        for (const d of pages) {
          const w = d.wave || 1
          if (!waves[w]) waves[w] = []
          waves[w].push(d)
        }
        const waveKeys = Object.keys(waves).map(Number).sort((a, b) => a - b)

        return (
          <div className="space-y-3">
            {waveKeys.map(waveNum => {
              const items = waves[waveNum]
              const waveSending = items.filter(d => d.status === 'sending' || d.status === 'syncing')
              const wavePending = items.filter(d => d.status === 'pending')
              const waveDone = items.filter(d => d.status === 'completed' || d.status === 'failed')
              const waveStartsAt = items[0]?.waveStartsAt
              const isWaiting = wavePending.length === items.length
              const isWaveDone = waveDone.length === items.length

              return (
                <div key={waveNum}>
                  {hasWaves && (
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-[10px] font-semibold text-muted-foreground/50 uppercase tracking-wider">
                        Wave {waveNum}/{maxWaves}
                      </span>
                      {isWaveDone ? (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-500 font-medium">{t('Concluida')}</span>
                      ) : isWaiting && waveStartsAt ? (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-500 font-medium">
                          {t('Inicia')} {new Date(waveStartsAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      ) : waveSending.length > 0 ? (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">{t('Enviando')}</span>
                      ) : null}
                    </div>
                  )}
                  <div className="space-y-1">
                    {items.map(d => {
                      const proc = d.processed || 0
                      const pagePct = d.total > 0 ? Math.round((proc / d.total) * 100) : 0
                      const isPending = d.status === 'pending'
                      const isDone = d.status === 'completed' || d.status === 'failed'
                      const pageFailed = d.failed || 0
                      const hasFailReason = d.status === 'failed' && d.failReason
                      return (
                        <div key={d.id}>
                          {hasFailReason ? (
                            <div className="flex items-center gap-2.5 py-2 px-3 rounded-lg bg-red-500/10 border border-red-500/20">
                              <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <span className="text-[11px] font-semibold text-red-500 block truncate">{d.account || '...'}</span>
                                <span className="text-[10px] text-red-400/80">{d.failMessage || 'Erro desconhecido'}</span>
                              </div>
                              {d.failReason === 'token_expired' && d.reconnectUrl && (
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(d.reconnectUrl!)
                                    toast.success('Link copiado! Cole no perfil Chrome correto.')
                                  }}
                                  className="flex items-center gap-1 text-[9px] px-2 py-1 rounded-md bg-red-500/20 text-red-400 font-semibold hover:bg-red-500/30 whitespace-nowrap"
                                  title={d.profiles?.length ? `Perfis: ${d.profiles.join(', ')}` : 'Copiar link de reconexão'}
                                >
                                  <Copy className="w-3 h-3" />
                                  Copiar link
                                </button>
                              )}
                            </div>
                          ) : (
                          <div className={cn('flex items-center gap-2.5 py-1.5 px-3 rounded-lg', isDone ? 'bg-emerald-500/5' : isPending ? 'bg-muted/5 opacity-50' : 'bg-muted/10')}>
                            {isDone ? (
                              <CheckCircle className="w-1.5 h-1.5 text-emerald-500 shrink-0" />
                            ) : isPending ? (
                              <Clock className="w-1.5 h-1.5 text-muted-foreground/40 shrink-0" />
                            ) : (
                              <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse shrink-0" />
                            )}
                            <span className="text-[11px] font-medium text-muted-foreground flex-1 truncate">{d.account || '...'}</span>
                            <span className="text-[10px] tabular-nums">
                              <span className="text-emerald-500">{d.sent.toLocaleString()}</span>
                              {pageFailed > 0 && <span className="text-amber-500/60"> · {pageFailed.toLocaleString()} ind.</span>}
                              <span className="text-muted-foreground/40"> / {d.total.toLocaleString()}</span>
                            </span>
                            <div className="w-16 h-1 bg-muted/20 rounded-full overflow-hidden shrink-0">
                              <div className={cn('h-full rounded-full', isDone ? 'bg-emerald-500/70' : 'bg-primary/70')} style={{ width: `${pagePct}%` }} />
                            </div>
                            <span className="text-[10px] text-muted-foreground/40 tabular-nums w-7 text-right">{pagePct}%</span>
                          </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        )
      })()}
    </div>
  )
}

/* ========================================================================
   FORMATTING TOOLBAR — wraps selection with Messenger markers
   ======================================================================== */

function FormattingToolbar({ textareaRef, value, onChange }: { textareaRef: React.RefObject<HTMLTextAreaElement | null>; value: string; onChange: (v: string) => void }) {
  const wrap = (marker: string, placeholder: string) => {
    const ta = textareaRef.current
    if (!ta) return
    const start = ta.selectionStart
    const end = ta.selectionEnd
    const selected = value.substring(start, end)
    const text = selected || placeholder
    const before = value.substring(0, start)
    const after = value.substring(end)
    const newVal = before + marker + text + marker + after
    onChange(newVal)
    setTimeout(() => {
      ta.focus()
      const cursorPos = selected ? end + marker.length * 2 : start + marker.length
      const selectEnd = selected ? cursorPos : cursorPos + text.length
      ta.setSelectionRange(selected ? selectEnd : cursorPos, selectEnd)
    }, 0)
  }

  return (
    <div className="flex gap-1 mb-1">
      <button type="button" onClick={() => wrap('*', 'negrito')} className="h-6 w-6 rounded text-xs font-bold bg-muted/40 hover:bg-primary/10 hover:text-primary transition-colors" title="Negrito">B</button>
      <button type="button" onClick={() => wrap('_', 'italico')} className="h-6 w-6 rounded text-xs italic bg-muted/40 hover:bg-primary/10 hover:text-primary transition-colors" title="Italico">I</button>
      <button type="button" onClick={() => wrap('~', 'riscado')} className="h-6 w-6 rounded text-xs line-through bg-muted/40 hover:bg-primary/10 hover:text-primary transition-colors" title="Riscado">S</button>
    </div>
  )
}

/* ========================================================================
   VARIABLE CHIPS — clickable chips to insert variables into textarea
   ======================================================================== */

function VariableChips({ onInsert }: { onInsert: (v: string) => void }) {
  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {['{{nome}}', '{{primeiro_nome}}', '{{saudacao}}', '{{hora}}', '{{dia}}', '{{data}}'].map((v) => (
        <span
          key={v}
          className="rounded-md bg-muted/40 px-1.5 py-0.5 text-[9px] text-muted-foreground cursor-pointer hover:bg-primary/10 hover:text-primary transition-colors"
          onClick={() => onInsert(v)}
        >
          {v}
        </span>
      ))}
      <span className="rounded-md bg-primary/10 border border-primary/20 px-1.5 py-0.5 text-[9px] text-primary">
        {'{oi|ola}'} = variacao
      </span>
    </div>
  )
}

/* ========================================================================
   EMOJI LIST — simple clickable emoji picker grid
   ======================================================================== */

const EMOJI_OPTIONS = [
  '\ud83d\udcf9', '\ud83d\udcde', '\ud83c\udfa4', '\ud83d\udd14', '\ud83c\udf81', '\u26a0\ufe0f',
  '\ud83d\udcf8', '\ud83d\ude80', '\ud83d\udd25', '\u26a1', '\ud83d\udcac', '\ud83d\udc9c',
  '\ud83d\udd12', '\ud83d\udcf1', '\ud83c\udf1f', '\ud83d\udca1', '\ud83c\udfaf', '\ud83d\udcb0',
  '\ud83d\udce3', '\ud83d\udc8e', '\ud83e\udde0', '\ud83d\udd1d', '\u2705', '\ud83c\udfc6',
]

/* ========================================================================
   PRESET EDITOR MODAL — create or edit a broadcast preset
   ======================================================================== */

function PresetEditorModal({
  open, onOpenChange, preset, t, language,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  preset: BroadcastPreset | null // null = create mode
  t: (s: string) => string
  language: string
}) {
  const createPreset = useCreatePreset()
  const updatePreset = useUpdatePreset()
  const deletePreset = useDeletePreset()

  const [emoji, setEmoji] = useState(preset?.emoji || '\ud83d\udce3')
  const [name, setName] = useState(preset?.name || '')
  const [mode, setMode] = useState<'text' | 'card'>(preset?.mode || 'text')
  const [reach, setReach] = useState<'normal' | 'max'>(preset?.reach || 'max')
  const [message, setMessage] = useState(preset?.message || '')
  const [imageUrl, setImageUrl] = useState(preset?.imageUrl || '')
  const [imagePreview, setImagePreview] = useState(preset?.imageUrl || '')
  const [cardTitle, setCardTitle] = useState(preset?.cardTitle || '')
  const [cardSubtitle, setCardSubtitle] = useState(preset?.cardSubtitle || '')
  const [buttons, setButtons] = useState<Array<{ text: string; type?: string; value?: string }>>(
    preset?.buttons?.length ? preset.buttons : []
  )
  const [uploading, setUploading] = useState(false)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [filters, setFilters] = useState<string[]>(['player'])
  const toggleFilter = (f: string) => setFilters(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f])
  const [antiFingerprint, setAntiFingerprint] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const messageRef = useRef<HTMLTextAreaElement>(null)

  // Reset state when modal opens with new preset
  useEffect(() => {
    if (open) {
      setEmoji(preset?.emoji || '\ud83d\udce3')
      setName(preset?.name || '')
      setMode(preset?.mode || 'text')
      setReach(preset?.reach || 'max')
      setMessage(preset?.message || '')
      setImageUrl(preset?.imageUrl || '')
      setImagePreview(preset?.imageUrl || '')
      setCardTitle(preset?.cardTitle || '')
      setCardSubtitle(preset?.cardSubtitle || '')
      setButtons(preset?.buttons?.length ? preset.buttons : [])
      setShowEmojiPicker(false)
    }
  }, [open, preset?.id])

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const preview = URL.createObjectURL(file)
      setImagePreview(preview)
      const data = await api.upload(file, { filterType: filters.length > 0 ? filters : 'none', antiFingerprint })
      const fullUrl = data.url.startsWith('http') ? data.url : `${window.location.origin}${data.url}`
      setImageUrl(fullUrl)
      toast.success(t('Imagem enviada!'))
    } catch {
      toast.error(t('Erro ao enviar imagem'))
      setImagePreview('')
      setImageUrl('')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleSave = async () => {
    if (!name.trim()) { toast.error(t('Digite um nome para o preset')); return }
    if (mode === 'text' && !message.trim()) { toast.error(t('Digite a mensagem')); return }
    if (mode === 'card' && !cardTitle.trim()) { toast.error(t('Digite o titulo do card')); return }

    const payload: Partial<BroadcastPreset> = {
      name: name.trim(),
      emoji,
      mode,
      reach,
      message: message.trim() || null,
      imageUrl: imageUrl || null,
      cardTitle: mode === 'card' ? cardTitle.trim() : null,
      cardSubtitle: mode === 'card' ? cardSubtitle.trim() || null : null,
      buttons: buttons.filter(b => b.text?.trim()),
    }

    try {
      if (preset?.id) {
        await updatePreset.mutateAsync({ id: preset.id, ...payload })
        toast.success(t('Preset atualizado'))
      } else {
        await createPreset.mutateAsync(payload)
        toast.success(t('Preset criado'))
      }
      onOpenChange(false)
    } catch {
      toast.error(t('Erro ao salvar preset'))
    }
  }

  const handleDelete = async () => {
    if (!preset?.id) return
    if (!confirm(t('Excluir este preset?'))) return
    try {
      await deletePreset.mutateAsync(preset.id)
      toast.success(t('Preset excluido'))
      onOpenChange(false)
    } catch {
      toast.error(t('Erro ao excluir preset'))
    }
  }

  const addButton = () => {
    if (buttons.length >= 3) return
    setButtons(prev => [...prev, { text: '', type: 'url', value: '' }])
  }

  const isSaving = createPreset.isPending || updatePreset.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{preset?.id ? t('Editar Preset') : t('Novo Preset')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Emoji + Name */}
          <div className="flex items-start gap-3">
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowEmojiPicker(v => !v)}
                className="flex h-12 w-12 items-center justify-center rounded-xl border border-border/40 bg-muted/20 text-2xl hover:border-primary/40 transition-colors shrink-0"
              >
                {emoji}
              </button>
              {showEmojiPicker && (
                <div className="absolute top-14 left-0 z-50 grid grid-cols-6 gap-1 rounded-xl border border-border/40 bg-background p-2 shadow-lg">
                  {EMOJI_OPTIONS.map(e => (
                    <button
                      key={e}
                      type="button"
                      onClick={() => { setEmoji(e); setShowEmojiPicker(false) }}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-lg hover:bg-primary/10 transition-colors"
                    >
                      {e}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex-1">
              <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Nome')}</Label>
              <Input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={t('Ex: Chamada perdida, Promo VIP...')}
                className="h-9 text-sm rounded-lg border-border/40"
              />
            </div>
          </div>

          {/* Mode toggle: Texto | Card */}
          <div>
            <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Tipo')}</Label>
            <div className="flex gap-1 p-0.5 rounded-lg bg-muted/30 w-fit">
              <button type="button" onClick={() => setMode('text')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${mode === 'text' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>{t('Texto')}</button>
              <button type="button" onClick={() => { setMode('card'); if (buttons.length === 0) setButtons([{ text: '', type: 'url', value: '' }]) }} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${mode === 'card' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>Card</button>
            </div>
          </div>

          {/* Reach toggle: Normal | Maximo */}
          <div>
            <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Alcance')}</Label>
            <div className="flex gap-1 p-0.5 rounded-lg bg-muted/30 w-fit">
              <button type="button" onClick={() => setReach('normal')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${reach === 'normal' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>{t('Normal')}</button>
              <button type="button" onClick={() => setReach('max')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${reach === 'max' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>{t('Maximo')}</button>
            </div>
          </div>

          {/* Card-specific fields */}
          {mode === 'card' && (
            <>
              {/* Card image area */}
              <div>
                <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Imagem do card')} *</Label>
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleUpload} className="hidden" />
                {imagePreview ? (
                  <div className="relative inline-block">
                    <img src={imagePreview} alt="" className="h-28 w-full max-w-[280px] rounded-xl object-cover border border-border/40" />
                    {(filters.includes('blur') || filters.includes('sensitive')) && <div className="absolute inset-0 rounded-xl backdrop-blur-sm pointer-events-none" />}
                    {filters.includes('sensitive') && <div className="absolute inset-0 rounded-xl bg-black/45 pointer-events-none" />}
                    {filters.includes('player') && !filters.includes('sensitive') && <div className="absolute inset-0 rounded-xl bg-black/18 pointer-events-none" />}
                    {filters.includes('player') && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <Play className="h-16 w-16 text-white fill-white drop-shadow-lg" />
                      </div>
                    )}
                    {filters.includes('sensitive') && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <EyeOff className="h-10 w-10 text-white/70 drop-shadow-lg" />
                      </div>
                    )}
                    {filters.includes('click') && (
                      <div className="absolute inset-0 flex items-center justify-start pl-4 pointer-events-none">
                        <svg viewBox="0 0 90 90" className="h-12 w-12 drop-shadow-lg" fill="white" opacity="0.9"><path d="M66.4 24.9c.4-1.9 0-3.8-1.1-5.3-1.1-1.5-2.8-2.5-4.7-2.7-1.9-.2-3.9.3-5.5 1.5L22.5 42.2l.2-8.5c0-1.9-.7-3.6-2-4.9-1.4-1.3-3.3-2-5.3-1.9-4 .2-7.3 3.6-7.4 7.5L7.4 65.1c-.1 5.5 1.5 10.7 4.6 14.9 4.8 6.6 12.4 10 20.4 10 5.9 0 11.9-1.9 17.2-5.7l13.7-10c3.3-2.5 4.2-7 1.9-10.1-1.1-1.5-2.8-2.5-4.7-2.7l.4-.3c1.6-1.2 2.7-2.9 3.1-4.8.4-1.9 0-3.8-1.1-5.3-1.1-1.5-2.8-2.5-4.7-2.7-.4 0-.7 0-1.1 0 .4-.7.8-1.6.9-2.4.4-1.9 0-3.8-1.1-5.3-1.2-1.6-2.9-2.5-4.8-2.7l11.5-8.4c1.6-1.2 2.7-2.9 3.1-4.8z"/><path d="M53 14.9c-.3 0-.6-.1-.8-.4l-6.4-9.2c-.3-.5-.2-1.1.2-1.4.5-.3 1.1-.2 1.4.2l6.4 9.2c.3.5.2 1.1-.2 1.4-.2.1-.4.2-.6.2z"/><path d="M72.3 42.6c-.3 0-.6-.1-.8-.4l-6.4-9.2c-.3-.5-.2-1.1.2-1.4.5-.3 1.1-.2 1.4.2l6.4 9.2c.3.5.2 1.1-.2 1.4-.2.1-.4.2-.6.2z"/><path d="M70.4 17.1c-.3 0-.6-.1-.8-.4-.3-.5-.2-1.1.2-1.4l9.2-6.4c.5-.3 1.1-.2 1.4.2.3.5.2 1.1-.2 1.4l-9.2 6.4c-.2.1-.4.2-.6.2z"/><path d="M81.6 28.1l-11.1-2c-.5-.1-.9-.6-.8-1.2.1-.5.6-.9 1.2-.8l11.1 2c.5.1.9.6.8 1.2-.1.4-.5.8-1.2.8z"/><path d="M61.5 13.1c-.5-.1-.9-.6-.8-1.2l2-11.1c.1-.5.6-.9 1.2-.8.5.1.9.6.8 1.2l-2 11.1c-.1.4-.5.8-1.2.8z"/></svg>
                      </div>
                    )}
                    <button type="button" onClick={() => { setImageUrl(''); setImagePreview('') }} className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow-sm">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="flex h-28 w-full max-w-[280px] items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border/40 text-xs text-muted-foreground hover:border-primary/40 hover:bg-primary/5 transition-all">
                    {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> {t('Enviando...')}</> : <><ImagePlus className="h-5 w-5" /> {t('Clique para enviar')}</>}
                  </button>
                )}
              </div>
              <div>
                <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Titulo')} *</Label>
                <Input value={cardTitle} onChange={e => setCardTitle(e.target.value)} placeholder={t('Promocao especial!')} maxLength={80} className="h-9 text-sm rounded-lg border-border/40" />
                <span className="text-[9px] text-muted-foreground">{cardTitle.length}/80</span>
              </div>
              <div>
                <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Subtitulo')}</Label>
                <Input value={cardSubtitle} onChange={e => setCardSubtitle(e.target.value)} placeholder={t('So hoje com 50% off')} maxLength={80} className="h-9 text-sm rounded-lg border-border/40" />
                <span className="text-[9px] text-muted-foreground">{cardSubtitle.length}/80</span>
              </div>
            </>
          )}

          {/* Text message area */}
          <div>
            <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
              {mode === 'card' ? t('Texto alternativo (IG/fallback)') : t('Mensagem')}
            </Label>
            <FormattingToolbar textareaRef={messageRef} value={message} onChange={setMessage} />
            <Textarea
              ref={messageRef}
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder={mode === 'card' ? t('Texto para Instagram/fallback') : t('Ola {{nome}}, tudo bem?')}
              className="min-h-[100px] resize-none text-sm rounded-xl border-border/40 bg-muted/20 focus:bg-transparent transition-colors"
            />
            <VariableChips onInsert={v => setMessage(prev => prev + v)} />
          </div>

          {/* Image upload for text mode */}
          {mode === 'text' && (
            <div>
              <div className="mb-2 space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Imagem (opcional)')}</Label>
                  {!imagePreview && (
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <Checkbox checked={antiFingerprint} onCheckedChange={(v) => setAntiFingerprint(!!v)} />
                      <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Shield className="h-3 w-3" /> Anti-RM</span>
                    </label>
                  )}
                </div>
                {!imagePreview && (
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <span className="text-[10px] text-muted-foreground">{t('Filtros:')}</span>
                    {[
                      { key: 'player', label: 'Play', icon: <Play className="h-2.5 w-2.5" /> },
                      { key: 'blur', label: 'Blur' },
                      { key: 'click', label: 'Clique' },
                      { key: 'sensitive', label: 'Sensivel', icon: <EyeOff className="h-2.5 w-2.5" /> },
                    ].map(f => (
                      <label key={f.key} className="flex items-center gap-1 cursor-pointer">
                        <Checkbox checked={filters.includes(f.key)} onCheckedChange={() => toggleFilter(f.key)} />
                        <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">{f.icon}{f.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <input ref={mode === 'text' ? fileInputRef : undefined} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleUpload} className="hidden" />
              {imagePreview ? (
                <div className="relative inline-block">
                  <img src={imagePreview} alt="" className="h-16 w-16 rounded-xl object-cover border border-border/40" />
                  {(filters.includes('blur') || filters.includes('sensitive')) && <div className="absolute inset-0 h-16 w-16 rounded-xl backdrop-blur-sm pointer-events-none" />}
                  {filters.includes('sensitive') && <div className="absolute inset-0 h-16 w-16 rounded-xl bg-black/45 pointer-events-none" />}
                  {filters.includes('player') && !filters.includes('sensitive') && <div className="absolute inset-0 h-16 w-16 rounded-xl bg-black/18 pointer-events-none" />}
                  {filters.includes('player') && (
                    <div className="absolute inset-0 h-16 w-16 flex items-center justify-center pointer-events-none">
                      <Play className="h-10 w-10 text-white fill-white drop-shadow-lg" />
                    </div>
                  )}
                  {filters.includes('sensitive') && (
                    <div className="absolute inset-0 h-16 w-16 flex items-center justify-center pointer-events-none">
                      <EyeOff className="h-6 w-6 text-white/70" />
                    </div>
                  )}
                  {filters.includes('click') && (
                    <div className="absolute inset-0 h-16 w-16 flex items-center justify-start pl-0.5 pointer-events-none">
                      <svg viewBox="0 0 90 90" className="h-6 w-6 drop-shadow-lg" fill="white" opacity="0.9"><path d="M66.4 24.9c.4-1.9 0-3.8-1.1-5.3-1.1-1.5-2.8-2.5-4.7-2.7-1.9-.2-3.9.3-5.5 1.5L22.5 42.2l.2-8.5c0-1.9-.7-3.6-2-4.9-1.4-1.3-3.3-2-5.3-1.9-4 .2-7.3 3.6-7.4 7.5L7.4 65.1c-.1 5.5 1.5 10.7 4.6 14.9 4.8 6.6 12.4 10 20.4 10 5.9 0 11.9-1.9 17.2-5.7l13.7-10c3.3-2.5 4.2-7 1.9-10.1-1.1-1.5-2.8-2.5-4.7-2.7l.4-.3c1.6-1.2 2.7-2.9 3.1-4.8.4-1.9 0-3.8-1.1-5.3-1.1-1.5-2.8-2.5-4.7-2.7-.4 0-.7 0-1.1 0 .4-.7.8-1.6.9-2.4.4-1.9 0-3.8-1.1-5.3-1.2-1.6-2.9-2.5-4.8-2.7l11.5-8.4c1.6-1.2 2.7-2.9 3.1-4.8z"/><path d="M53 14.9l-6.4-9.2c-.3-.5-.2-1.1.2-1.4.5-.3 1.1-.2 1.4.2l6.4 9.2c.3.5.2 1.1-.2 1.4z"/><path d="M72.3 42.6l-6.4-9.2c-.3-.5-.2-1.1.2-1.4.5-.3 1.1-.2 1.4.2l6.4 9.2c.3.5.2 1.1-.2 1.4z"/><path d="M70.4 17.1c-.3-.5-.2-1.1.2-1.4l9.2-6.4c.5-.3 1.1-.2 1.4.2.3.5.2 1.1-.2 1.4l-9.2 6.4z"/></svg>
                    </div>
                  )}
                  <button type="button" onClick={() => { setImageUrl(''); setImagePreview('') }} className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-destructive-foreground">
                    <X className="h-2.5 w-2.5" />
                  </button>
                </div>
              ) : (
                <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="flex h-16 w-16 items-center justify-center rounded-xl border-2 border-dashed border-border/40 text-muted-foreground hover:border-primary/40 hover:bg-primary/5 transition-all">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                </button>
              )}
            </div>
          )}

          {/* Buttons */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Link className="h-3 w-3" /> {t('Botoes (max 3)')}
              </Label>
              {buttons.length < 3 && (
                <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2 gap-1 rounded-lg hover:bg-primary/10 hover:text-primary" onClick={addButton}>
                  {t('+ Adicionar')}
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {buttons.map((btn, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    value={btn.text || ''}
                    onChange={e => setButtons(prev => prev.map((b, i) => i === idx ? { ...b, text: e.target.value } : b))}
                    placeholder={t('Texto do botao')}
                    className="h-8 text-xs rounded-lg border-border/40 flex-1"
                  />
                  <Input
                    value={btn.value || ''}
                    onChange={e => setButtons(prev => prev.map((b, i) => i === idx ? { ...b, value: e.target.value } : b))}
                    placeholder="https://..."
                    className="h-8 text-xs rounded-lg border-border/40 flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => setButtons(prev => prev.filter((_, i) => i !== idx))}
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between gap-2">
          <div>
            {preset?.id && !preset.isGlobal && (
              <Button variant="ghost" size="sm" onClick={handleDelete} disabled={deletePreset.isPending} className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-1">
                <Trash2 className="h-3 w-3" /> {t('Excluir')}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <DialogClose asChild>
              <Button variant="outline" size="sm">{t('Cancelar')}</Button>
            </DialogClose>
            <Button size="sm" onClick={handleSave} disabled={isSaving} className="gap-1.5">
              {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />}
              {t('Salvar')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/* ========================================================================
   TRIAL GATE — blocks broadcast for expired trials
   ======================================================================== */

function TrialGate({ trialEndsAt, onContinue }: { trialEndsAt: string; onContinue?: () => void }) {
  const diff = Math.ceil((new Date(trialEndsAt).getTime() - Date.now()) / 86400000)
  const expired = diff <= 0

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-full max-w-md text-center space-y-6">
        <div className={cn(
          'mx-auto flex h-20 w-20 items-center justify-center rounded-2xl',
          expired ? 'bg-red-500/10' : 'bg-emerald-500/10'
        )}>
          {expired ? (
            <XCircle className="h-10 w-10 text-red-500" />
          ) : (
            <Clock className="h-10 w-10 text-emerald-500" />
          )}
        </div>

        {expired ? (
          <>
            <div>
              <h2 className="text-2xl font-bold">Periodo de teste encerrado</h2>
              <p className="mt-2 text-muted-foreground">
                Seu acesso gratuito expirou. Entre em contato para ativar seu plano e continuar usando o broadcast.
              </p>
            </div>
            <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4">
              <p className="text-sm text-red-400 font-medium">Acesso bloqueado</p>
            </div>
          </>
        ) : (
          <>
            <div>
              <h2 className="text-2xl font-bold">Periodo de teste gratuito</h2>
              <p className="mt-2 text-muted-foreground">
                Voce tem acesso completo a todas as funcionalidades durante o periodo de avaliacao.
              </p>
            </div>
            <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-5">
              <p className="text-4xl font-bold text-emerald-500">{diff}</p>
              <p className="text-sm text-emerald-400 font-medium mt-1">{diff === 1 ? 'dia restante' : 'dias restantes'}</p>
            </div>
            {onContinue && (
              <Button onClick={onContinue} size="lg" className="w-full">
                <Play className="h-4 w-4 mr-2" /> Continuar para o Broadcast
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

/* ========================================================================
   EXPIRED TOKENS ALERT — shows pages that need reconnection
   ======================================================================== */

function ExpiredTokensAlert({ t }: { t: (s: string) => string }) {
  const [pages, setPages] = useState<Array<{ igUserId: string; name: string; profiles: string[]; reconnectUrl: string }>>([])
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    api.get('/broadcast/expired-tokens').then(setPages).catch(() => {})
  }, [])

  if (dismissed || pages.length === 0) return null

  return (
    <div className="px-6 md:px-8 py-2">
      <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
            <span className="text-xs font-semibold text-amber-500">
              {t('Reconectar Paginas')} ({pages.length})
            </span>
          </div>
          <button onClick={() => setDismissed(true)} className="text-muted-foreground/40 hover:text-muted-foreground">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="space-y-1 max-h-40 overflow-y-auto">
          {pages.map(p => (
            <div key={p.igUserId} className="flex items-center gap-2 py-1 px-2 rounded-lg bg-amber-500/5 hover:bg-amber-500/10">
              <div className="flex-1 min-w-0">
                <span className="text-[11px] font-medium text-foreground block truncate">{p.name}</span>
                {p.profiles.length > 0 && (
                  <span className="text-[10px] text-muted-foreground/60">{p.profiles.join(', ')}</span>
                )}
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(p.reconnectUrl)
                  toast.success(`Link copiado para "${p.name}"`)
                }}
                className="flex items-center gap-1 text-[9px] px-2 py-1 rounded-md bg-amber-500/15 text-amber-600 dark:text-amber-400 font-semibold hover:bg-amber-500/25 whitespace-nowrap shrink-0"
              >
                <Copy className="w-3 h-3" />
                {t('Copiar link')}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/* ========================================================================
   eCPM SUGGESTION CARD
   ======================================================================== */

function EcpmSuggestionCard({ onApplyTimes, t }: { onApplyTimes: (times: string[]) => void; t: (s: string) => string }) {
  const peakHours = useGamPeakHours()
  const [dismissed, setDismissed] = useState(false)

  if (dismissed || !peakHours.data?.hasData) return null

  const { hours, avgEcpm, period } = peakHours.data
  if (hours.length === 0) return null

  const topTimes = hours.map(h => h.time)

  return (
    <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 space-y-2.5">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-amber-500/10 flex items-center justify-center">
            <TrendingUp className="h-4 w-4 text-amber-500" />
          </div>
          <div>
            <p className="text-xs font-semibold">{t('Melhores horários — eCPM × Tráfego')}</p>
            <p className="text-[10px] text-muted-foreground">
              {t('eCPM médio')}: ${avgEcpm} · {period.from.split('-').reverse().join('/')} — {period.to.split('-').reverse().join('/')}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="p-1 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {hours.map((h, i) => (
          <div key={h.hour} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-background border border-border/30">
            <span className="text-xs font-mono font-semibold">{h.time}</span>
            <span className="text-[10px] text-amber-600 font-medium">${h.ecpm}</span>
            <span className="text-[10px] text-muted-foreground">{h.views >= 1000 ? `${(h.views / 1000).toFixed(1)}k` : h.views} views</span>
            {i === 0 && <span className="text-[9px] bg-amber-500/15 text-amber-700 px-1 rounded font-medium">melhor</span>}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={() => onApplyTimes(topTimes)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/10 text-amber-600 text-[11px] font-medium hover:bg-amber-500/20 transition-colors"
        >
          <Zap className="h-3 w-3" /> {t('Usar esses horários na campanha')}
        </button>
        <p className="text-[10px] text-muted-foreground">{t('Abre o modal com os horários de pico preenchidos')}</p>
      </div>
    </div>
  )
}

/* ========================================================================
   MAIN BROADCAST PAGE
   ======================================================================== */

export function Broadcast() {
  const { t, language } = useLanguage()
  const { data: user } = useAuth()
  const { data: config, isLoading } = useBroadcastConfig()
  const { data: historyData } = useBroadcastHistory()
  const { data: presets, isLoading: presetsLoading } = usePresets()
  const deleteBroadcast = useDeleteBroadcast()
  const clearHistory = useClearBroadcastHistory()
  const { data: schedules } = useBroadcastSchedules()
  const createSchedule = useCreateSchedule()
  const updateSchedule = useUpdateSchedule()
  const deleteSchedule = useDeleteSchedule()
  const toggleSchedule = useToggleSchedule()
  const clearSchedules = useClearSchedules()
  const qc = useQueryClient()

  /* ---- General UI state ---- */
  const [trialPassed, setTrialPassed] = useState(false)
  const [activeTab, setActiveTab] = useState<'presets' | 'escrever' | 'templates' | 'historico' | 'campanha'>('historico')

  /* ---- Pages selection ---- */
  const [showIgAccounts, setShowIgAccounts] = useState(false)
  const [hideFailedAccounts, setHideFailedAccounts] = useState(true)
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([])
  const [pageSearch, setPageSearch] = useState('')

  /* ---- Audience filter ---- */
  const [filter, setFilter] = useState<'all' | '24h' | 'tag'>('all')
  const [filterTag, setFilterTag] = useState('')

  /* ---- Reach / message tag ---- */
  const [messageTag, setMessageTag] = useState('UTILITY')

  /* ---- Preset selection (Tab 1) ---- */
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null)
  const [presetModalOpen, setPresetModalOpen] = useState(false)
  const [editingPreset, setEditingPreset] = useState<BroadcastPreset | null>(null)

  /* ---- Compose form state (Tab 2 "Escrever") ---- */
  const [messageMode, setMessageMode] = useState<'text' | 'card'>('text')
  const [cardTitle, setCardTitle] = useState('')
  const [cardSubtitle, setCardSubtitle] = useState('')
  const [message, setMessage] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [imagePreview, setImagePreview] = useState('')
  const [uploading, setUploading] = useState(false)
  const [filters, setFilters] = useState<string[]>(['player'])
  const toggleFilter = (f: string) => setFilters(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f])
  const [antiFingerprint, setAntiFingerprint] = useState(false)
  const [buttons, setButtons] = useState<{ text: string; type: 'url' | 'postback' | 'quick_reply'; value: string }[]>([])

  /* ---- Scheduling ---- */
  const [scheduleEnabled, setScheduleEnabled] = useState(false)
  const [scheduledDate, setScheduledDate] = useState('')
  const [scheduledTime, setScheduledTime] = useState('')
  const [scheduledTimes, setScheduledTimes] = useState<string[]>(['10:00'])
  const addTime = () => setScheduledTimes(prev => [...prev, ''])
  const removeTime = (i: number) => setScheduledTimes(prev => prev.filter((_, idx) => idx !== i))
  const updateTime = (i: number, v: string) => setScheduledTimes(prev => prev.map((t, idx) => idx === i ? v : t))
  const [repeatEnabled, setRepeatEnabled] = useState(false)
  const [repeatUntil, setRepeatUntil] = useState('')
  const [scheduleType, setScheduleType] = useState<'once' | 'recurring'>('once')
  const [selectedDays, setSelectedDays] = useState<number[]>([1, 3, 5])
  const [schedulePresetIds, setSchedulePresetIds] = useState<string[]>([])
  const [scheduleTemplateName, setScheduleTemplateName] = useState<string>('')
  const [editingScheduleId, setEditingScheduleId] = useState<string | null>(null)
  const { data: bcTemplates = [] } = useTemplates()

  // Abre a aba Agendar já preenchida com um template (usado pelo botão da aba Templates)
  const openScheduleForTemplate = (templateName: string) => {
    setEditingScheduleId(null)
    setScheduleEnabled(true)
    setScheduleTemplateName(templateName)
    setActiveTab('escrever')
  }

  // Carrega um agendamento existente no formulário da aba Agendar (modo edição)
  const loadScheduleForEdit = (s: any) => {
    setEditingScheduleId(s.id)
    setScheduleEnabled(true)
    setScheduleTemplateName(s.templateName || '')
    setScheduleType(s.scheduleType || 'once')
    if (s.scheduleType === 'recurring') {
      setSelectedDays(s.daysOfWeek || [])
      setScheduledTimes((s.timesOfDay?.length ? s.timesOfDay : (s.timeOfDay ? [s.timeOfDay] : ['00:10'])))
      setRepeatUntil(s.endsAt ? new Date(s.endsAt).toISOString().split('T')[0] : '')
    } else {
      setScheduledTime(s.timeOfDay || (s.scheduledAt ? new Date(s.scheduledAt).toTimeString().slice(0, 5) : ''))
      setScheduledDate(s.scheduledAt ? new Date(s.scheduledAt).toISOString().split('T')[0] : '')
    }
    setSelectedAccounts(Array.isArray(s.accountIds) ? s.accountIds : [])
    setActiveTab('escrever')
  }
  const [scheduleRandomize, setScheduleRandomize] = useState(false)
  const togglePresetSelection = (id: string) => setSchedulePresetIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  const toggleDay = (d: number) => setSelectedDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d])
  const dayLabels = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S']

  /* ---- Campaign state ---- */
  const campaigns = useBroadcastCampaigns()
  const createCampaign = useCreateCampaign()
  const updateCampaign = useUpdateCampaign()
  const deleteCampaign = useDeleteCampaign()
  const toggleCampaign = useToggleCampaign()
  const [campaignModalOpen, setCampaignModalOpen] = useState(false)
  const [editingCampaign, setEditingCampaign] = useState<BroadcastCampaign | null>(null)
  const [cName, setCName] = useState('')
  const [cLink, setCLink] = useState('')
  const [cPhrasesText, setCPhrasesText] = useState('')
  const [cSubtitlesText, setCSubtitlesText] = useState('')
  const [cPresetIds, setCPresetIds] = useState<string[]>([])
  const [cImages, setCImages] = useState<string[]>([])
  const [cButtonTexts, setCButtonTexts] = useState<string[]>([''])
  const [cPreviewIdx, setCPreviewIdx] = useState<number | null>(null)
  const [cAccountIds, setCAccountIds] = useState<string[] | null>(null)
  const [cAllAccounts, setCAllAccounts] = useState(true)
  const [cTimes, setCTimes] = useState<string[]>(['10:00'])
  const [cDays, setCDays] = useState<number[]>([0,1,2,3,4,5,6])
  const [cUploadingIdx, setCUploadingIdx] = useState<number | null>(null)
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false)
  const [mediaFolders, setMediaFolders] = useState<{id: string; name: string; images: {id: string; url: string; name?: string}[]}[]>([])
  const [mediaPickerLoading, setMediaPickerLoading] = useState(false)
  const [mediaPickerSelected, setMediaPickerSelected] = useState<string[]>([])
  const [mediaPickerFolder, setMediaPickerFolder] = useState<string | null>(null)

  const openMediaPicker = async () => {
    setMediaPickerOpen(true)
    setMediaPickerSelected([])
    setMediaPickerLoading(true)
    try {
      const folders = await api.get<any[]>('/media-folders')
      setMediaFolders(folders || [])
      if (folders && folders.length > 0) setMediaPickerFolder(folders[0].id)
    } catch { /* ignore */ } finally {
      setMediaPickerLoading(false)
    }
  }

  const confirmMediaPicker = () => {
    setCImages(prev => {
      const existing = new Set(prev.filter(Boolean))
      const toAdd = mediaPickerSelected.filter(u => !existing.has(u))
      return [...prev.filter(Boolean), ...toAdd]
    })
    setMediaPickerOpen(false)
  }

  const toggleMediaImg = (url: string) => {
    setMediaPickerSelected(prev =>
      prev.includes(url) ? prev.filter(u => u !== url) : [...prev, url]
    )
  }

  const [cTimeMode, setCTimeMode] = useState<'auto' | 'manual'>('auto')
  const [genStart, setGenStart] = useState('06:00')
  const [genInterval, setGenInterval] = useState('160')
  const [genCount, setGenCount] = useState('9')

  const openCampaignModal = (campaign?: BroadcastCampaign) => {
    if (campaign) {
      setEditingCampaign(campaign)
      setCName(campaign.name)
      setCLink(campaign.whatsappLink)
      setCPhrasesText(campaign.phrases.join('\n'))
      setCSubtitlesText((campaign.subtitles || []).join('\n'))
      setCPresetIds(campaign.presetIds || [])
      setCImages(campaign.images.length > 0 ? campaign.images : [])
      setCButtonTexts(campaign.buttonTexts.length > 0 ? campaign.buttonTexts : [''])
      setCAllAccounts(!campaign.accountIds)
      setCAccountIds(campaign.accountIds)
      setCTimes(campaign.timesOfDay.length > 0 ? campaign.timesOfDay : ['10:00'])
      setCDays(campaign.daysOfWeek)
      setCPreviewIdx(null)
    } else {
      setEditingCampaign(null)
      setCName('')
      setCLink('')
      setCPhrasesText('')
      setCSubtitlesText('')
      setCPresetIds([])
      setCImages([])
      setCButtonTexts([''])
      setCAllAccounts(true)
      setCAccountIds(null)
      setCTimes(['10:00'])
      setCDays([0,1,2,3,4,5,6])
      setCPreviewIdx(null)
    }
    setCampaignModalOpen(true)
  }

  const generateTimes = () => {
    const [hh, mm] = genStart.split(':').map(Number)
    const interval = parseInt(genInterval) || 160
    const count = parseInt(genCount) || 9
    const times: string[] = []
    let totalMin = hh * 60 + mm
    for (let i = 0; i < count; i++) {
      const h = Math.floor((totalMin % 1440) / 60)
      const m = totalMin % 60
      times.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`)
      totalMin += interval
    }
    setCTimes(times)
  }

  const saveCampaign = async () => {
    const phrases = cPhrasesText.split('\n').map((s: string) => s.trim()).filter(Boolean)
    const subtitles = cSubtitlesText.split('\n').map((s: string) => s.trim()).filter(Boolean)
    const buttonTexts = cButtonTexts.map((s: string) => s.trim()).filter(Boolean)
    const images = cImages.filter(Boolean)
    const hasContent = phrases.length > 0 || cPresetIds.length > 0
    if (!cName.trim() || !cLink.trim() || !hasContent || cTimes.length === 0 || cDays.length === 0) {
      toast.error(t('Preencha nome, link, conteúdo (títulos ou presets), horários e dias'))
      return
    }
    if (phrases.length > 0 && buttonTexts.length === 0) {
      toast.error(t('Preencha ao menos um texto de botão para as frases manuais'))
      return
    }
    if (subtitles.length > 0 && subtitles.length !== phrases.length) {
      toast.error(t(`Subtítulos (${subtitles.length}) deve ter a mesma quantidade de títulos (${phrases.length})`))
      return
    }
    const payload = {
      name: cName.trim(),
      whatsappLink: cLink.trim(),
      phrases,
      subtitles: subtitles.length > 0 ? subtitles : [],
      presetIds: cPresetIds.length > 0 ? cPresetIds : [],
      images,
      buttonTexts,
      accountIds: cAllAccounts ? null : cAccountIds,
      timesOfDay: cTimes.filter(Boolean),
      daysOfWeek: cDays,
    }
    try {
      if (editingCampaign) {
        await updateCampaign.mutateAsync({ id: editingCampaign.id, ...payload })
        toast.success(t('Campanha atualizada'))
      } else {
        await createCampaign.mutateAsync(payload as any)
        toast.success(t('Campanha criada'))
      }
      setCampaignModalOpen(false)
    } catch {
      toast.error(t('Erro ao salvar campanha'))
    }
  }

  const handleCImageUpload = async (file: File, idx: number) => {
    setCUploadingIdx(idx)
    try {
      const data = await api.upload(file, { filterType: filters.length > 0 ? filters : 'none', antiFingerprint: false })
      const raw = (data as any).url || ''
      const url = raw.startsWith('http') ? raw : `${window.location.origin}${raw}`
      setCImages(prev => {
        const next = [...prev]
        if (idx < next.length) next[idx] = url
        else next.push(url)
        return next
      })
    } catch {
      toast.error(t('Erro no upload da imagem'))
    } finally {
      setCUploadingIdx(null)
    }
  }

  /* ---- History ---- */
  const [historyPage, setHistoryPage] = useState(1)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const historyPageSize = 25

  /* ---- Active broadcasts ---- */
  const [activeBroadcasts, setActiveBroadcasts] = useState<string[]>([])
  const [broadcastStats, setBroadcastStats] = useState<Record<string, BroadcastProgressData>>({})
  const [toastShown, setToastShown] = useState(false)

  /* ---- Sync contacts ---- */
  const [syncingContacts, setSyncingContacts] = useState(false)
  const [syncStatus, setSyncStatus] = useState<string>('')
  const [syncProgress, setSyncProgress] = useState<{ completed: number; total: number; pages: string[] }>({ completed: 0, total: 0, pages: [] })

  /* ---- Refs ---- */
  const fileInputRef = useRef<HTMLInputElement>(null)
  const messageTextareaRef = useRef<HTMLTextAreaElement>(null)

  /* ================================================================
     EFFECTS
     ================================================================ */

  // Restore active broadcasts on mount
  useEffect(() => {
    api.get<string[]>('/broadcast/active').then(ids => {
      if (ids && ids.length) setActiveBroadcasts(prev => {
        const existing = new Set(prev)
        const newIds = ids.filter(id => !existing.has(id))
        return newIds.length > 0 ? [...prev, ...newIds] : prev
      })
    }).catch(() => {})
  }, [])

  // Detect running broadcasts from history data
  useEffect(() => {
    if (!historyData) return
    const running = historyData
      .filter(r => r.status === 'pending' || r.status === 'sending' || r.status === 'syncing')
      .map(r => r.id)
    if (running.length > 0) {
      setActiveBroadcasts(prev => {
        const existing = new Set(prev)
        const newIds = running.filter(id => !existing.has(id))
        return newIds.length > 0 ? [...prev, ...newIds] : prev
      })
    }
  }, [historyData])

  // Aggregated toast when all broadcasts finish
  const aggregated = Object.values(broadcastStats)
  const totalSentAgg = aggregated.reduce((s, d) => s + d.sent_count, 0)
  const totalPagesAgg = aggregated.length
  const completedPagesAgg = aggregated.filter(d => d.status === 'completed' || d.status === 'failed').length

  useEffect(() => {
    if (totalPagesAgg > 0 && completedPagesAgg === totalPagesAgg && !toastShown) {
      setToastShown(true)
      const failedPages = aggregated.filter(d => d.status === 'failed').length
      if (failedPages === totalPagesAgg) {
        toast.error(t('Broadcast falhou'))
      } else {
        toast.success(`${t('Broadcast concluido!')} ${totalSentAgg} ${t('enviados')}${totalPagesAgg > 1 ? ` (${totalPagesAgg} ${t('paginas')})` : ''}`)
      }
    }
    if (totalPagesAgg === 0) setToastShown(false)
  }, [totalPagesAgg, completedPagesAgg, toastShown, totalSentAgg])

  /* ================================================================
     DERIVED DATA
     ================================================================ */

  const filteredAccounts = useMemo(() => {
    let list = config?.accounts.filter(a => {
      if (!showIgAccounts && a.igLogin) return false
      if (hideFailedAccounts && a.recentFail) return false
      return true
    }) || []
    if (pageSearch.trim()) {
      const q = pageSearch.trim().toLowerCase()
      list = list.filter(a => a.name.toLowerCase().includes(q))
    }
    return list
  }, [config?.accounts, showIgAccounts, hideFailedAccounts, pageSearch])

  const selectedAccountsList = config?.accounts.filter(a => selectedAccounts.includes(a.igUserId)) || []
  const isUtilityMode = messageTag === 'UTILITY'
  const totalAllContacts = config?.accounts.reduce((s, a) => s + (isUtilityMode ? (a.totalWithDead || a.totalContacts) : a.totalContacts), 0) || 0
  const totalSelectedContacts = selectedAccountsList.reduce((s, a) => s + (isUtilityMode ? (a.totalWithDead || a.totalContacts) : a.totalContacts), 0)

  const selectedPreset = useMemo(() => presets?.find(p => p.id === selectedPresetId) || null, [presets, selectedPresetId])

  const userPresets = useMemo(() => presets?.filter(p => !p.isGlobal) || [], [presets])
  const globalPresets = useMemo(() => presets?.filter(p => p.isGlobal) || [], [presets])

  // Which label to show in the bottom bar
  const presetLabel = activeTab === 'presets' && selectedPreset
    ? `${selectedPreset.emoji} ${selectedPreset.name}`
    : activeTab === 'escrever'
      ? t('Personalizado')
      : null

  // Can send?
  const canSend = useMemo(() => {
    if (selectedAccounts.length === 0) return false
    if (activeTab === 'presets') {
      return !!selectedPreset
    }
    if (activeTab === 'escrever') {
      if (messageMode === 'card') return !!(cardTitle.trim() && imageUrl && buttons.some(b => b.text.trim() && b.value?.trim()))
      return !!message.trim()
    }
    return false
  }, [selectedAccounts, activeTab, selectedPreset, messageMode, cardTitle, imageUrl, buttons, message])

  const dateFmtLocale = language === 'en' ? 'en-US' : 'pt-BR'

  /* ================================================================
     HANDLERS
     ================================================================ */

  const toggleAccount = (id: string) => {
    setSelectedAccounts(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  const handleBroadcastProgress = useCallback((id: string, data: BroadcastProgressData | null) => {
    if (!data) return
    setBroadcastStats(prev => ({ ...prev, [id]: data }))
  }, [])

  const handleSyncAllContacts = useCallback(async () => {
    if (syncingContacts) return
    setSyncingContacts(true)
    setSyncStatus('Iniciando...')
    setSyncProgress({ completed: 0, total: 0, pages: [] })

    try {
      const res = await fetch('/api/v2/broadcast/sync-all-contacts', {
        headers: { ...getAuthHeaders() },
      })
      if (!res.ok || !res.body) throw new Error('Failed')

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const data = JSON.parse(line.slice(6))
            if (data.status === 'completed') {
              setSyncStatus(`Pronto! ${data.totalImported} novos contatos, ${data.totalUpdated} atualizados`)
              toast.success(`Sync completo: ${data.totalImported} novos contatos`)
              setSyncingContacts(false)
              setSyncProgress({ completed: data.totalAccounts, total: data.totalAccounts, pages: [] })
              qc.invalidateQueries({ queryKey: ['broadcast-config'] })
            } else if (data.status === 'syncing') {
              setSyncStatus(data.message || 'Varrendo...')
              setSyncProgress(p => ({ ...p, completed: data.completedAccounts || p.completed, total: data.total || p.total }))
            } else if (data.status === 'page_done') {
              const label = `${data.page}: ${data.contactCount} contatos${data.imported ? ` (+${data.imported} novos)` : ''}`
              setSyncProgress(p => ({
                completed: data.completedAccounts || p.completed + 1,
                total: data.total || p.total,
                pages: [...p.pages.slice(-4), label],
              }))
            }
          } catch { /* ignore parse errors */ }
        }
      }
    } catch {
      toast.error('Erro ao sincronizar contatos')
    }
    setSyncingContacts(false)
  }, [syncingContacts, qc])

  // Simple mode upload handler
  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      setImagePreview(URL.createObjectURL(file))
      const data = await api.upload(file, { filterType: filters.length > 0 ? filters : 'none', antiFingerprint })
      const fullUrl = data.url.startsWith('http') ? data.url : `${window.location.origin}${data.url}`
      setImageUrl(fullUrl)
      toast.success(filters.length > 0 ? t('Imagem enviada com filtro!') : t('Imagem enviada!'))
    } catch {
      toast.error(t('Erro ao enviar imagem'))
      setImagePreview('')
      setImageUrl('')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const addButton = () => {
    if (buttons.length >= 3) return
    setButtons(prev => [...prev, { text: '', type: 'url', value: '' }])
  }
  const removeButton = (idx: number) => setButtons(prev => prev.filter((_, i) => i !== idx))
  const updateButton = (idx: number, field: string, val: string) => {
    setButtons(prev => prev.map((b, i) => i === idx ? { ...b, [field]: val } : b))
  }

  /* ---- SEND ---- */
  const handleSend = useCallback(async () => {
    if (selectedAccounts.length === 0) { toast.error(t('Selecione uma conta')); return }

    // Determine what to send based on active tab
    let sendMessage = ''
    let sendImageUrl: string | undefined
    let sendMode: string | undefined
    let sendTitle: string | undefined
    let sendSubtitle: string | undefined
    let sendButtons: { text: string; type: string; value: string }[] = []
    let sendR: number | undefined

    if (activeTab === 'presets' && selectedPreset) {
      sendMessage = selectedPreset.mode === 'card'
        ? (selectedPreset.cardTitle || '')
        : (selectedPreset.message || '')
      sendImageUrl = selectedPreset.imageUrl || undefined
      sendR = (selectedPreset.reach === 'max' || messageTag === 'UTILITY') ? 1 : undefined
      sendButtons = (selectedPreset.buttons || []).filter(b => b.text?.trim()).map(b => ({ text: b.text, type: b.type || 'url', value: b.value || '' }))
      if (selectedPreset.mode === 'card') {
        sendMode = 'card'
        sendTitle = selectedPreset.cardTitle || undefined
        sendSubtitle = selectedPreset.cardSubtitle || undefined
      }
    } else if (activeTab === 'escrever') {
      if (messageMode === 'card') {
        if (!cardTitle.trim()) { toast.error(t('Digite o titulo do card')); return }
        if (!imageUrl) { toast.error(t('Adicione uma imagem ao card')); return }
        sendMessage = cardTitle.trim()
        sendImageUrl = imageUrl || undefined
        sendMode = 'card'
        sendTitle = cardTitle.trim()
        sendSubtitle = cardSubtitle.trim() || undefined
        sendButtons = buttons.filter(b => b.text.trim() && (b.type === 'postback' || b.value.trim()))
        sendR = messageTag === 'UTILITY' ? 1 : undefined
      } else {
        if (!message.trim()) { toast.error(t('Digite a mensagem')); return }
        sendMessage = message.trim()
        sendImageUrl = imageUrl || undefined
        sendButtons = buttons.filter(b => b.text.trim() && (b.type === 'postback' || b.value.trim()))
        sendR = messageTag === 'UTILITY' ? 1 : undefined
      }
    } else {
      toast.error(t('Selecione um preset ou escreva uma mensagem'))
      return
    }

    const label = scheduleEnabled ? t('Confirma o agendamento do') : t('Confirma o envio')
    if (!confirm(`${label} broadcast?`)) return

    try {
      const scheduledAt = scheduleEnabled && scheduledDate && scheduledTime
        ? new Date(`${scheduledDate}T${scheduledTime}`).toISOString()
        : undefined

      const body: Record<string, unknown> = {
        igUserIds: selectedAccounts,
        filter,
        filterTag: filter === 'tag' ? filterTag : undefined,
        message: sendMessage,
        imageUrl: sendImageUrl,
        r: sendR,
        buttons: sendButtons.length > 0 ? sendButtons : undefined,
        scheduledAt,
        repeatType: repeatEnabled ? 'daily' : undefined,
        repeatUntil: repeatEnabled && repeatUntil ? new Date(`${repeatUntil}T23:59:59`).toISOString() : undefined,
        ...(sendMode === 'card' ? { mode: 'card', title: sendTitle, subtitle: sendSubtitle } : {}),
      }

      const result = await api.post('/broadcast/send', body) as { ok?: boolean; broadcastIds?: string[]; totalContacts?: number; scheduled?: boolean; error?: string }

      if (result.ok && result.broadcastIds) {
        if (result.scheduled) {
          toast.success(t('Broadcast agendado com sucesso!'))
        } else {
          toast.success(`${t('Broadcast iniciado!')} ${result.totalContacts} ${t('contatos')}`)
          setActiveBroadcasts(prev => [...prev, ...result.broadcastIds!])
        }
        // Ir pro histórico pra acompanhar
        setActiveTab('historico')
        // Reset compose form
        setMessage('')
        setCardTitle('')
        setCardSubtitle('')
        setMessageMode('text')
        setImageUrl('')
        setImagePreview('')
        setButtons([])
        setScheduleEnabled(false)
        setScheduledDate('')
        setScheduledTime('')
        setRepeatEnabled(false)
        setRepeatUntil('')
        setSelectedPresetId(null)
        qc.invalidateQueries({ queryKey: ['broadcast-history'] })
      } else {
        toast.error(result.error || t('Erro ao iniciar broadcast'))
      }
    } catch {
      toast.error(t('Erro ao enviar broadcast'))
    }
  }, [selectedAccounts, message, filter, filterTag, imageUrl, messageTag, buttons, activeTab, selectedPreset, messageMode, cardTitle, cardSubtitle, scheduleEnabled, scheduledDate, scheduledTime, repeatEnabled, repeatUntil, t, qc])

  /* ================================================================
     LOADING / TRIAL GATE
     ================================================================ */

  if (isLoading) {
    return (
      <div className="space-y-6 px-6 md:px-8 py-6">
        <Skeleton className="h-6 w-40 rounded-lg" />
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    )
  }

  if (user?.trialEndsAt && !trialPassed) {
    return <TrialGate trialEndsAt={user.trialEndsAt} onContinue={() => setTrialPassed(true)} />
  }

  /* ================================================================
     RENDER
     ================================================================ */

  return (
    <div className="flex flex-col h-full">
      <PermissionBadge permissions={[
        { name: 'pages_messaging', endpoint: 'POST /{page-id}/messages', description: 'Sends messages to Page subscribers. Used by the Broadcast feature to deliver content to followers who have interacted with the Page.' },
      ]} />

      {/* ====== HEADER ====== */}
      <div className="px-6 md:px-8 pt-5 pb-4 border-b border-border/30 bg-card/30 backdrop-blur-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5">
              <Megaphone className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight">{t('Broadcast')}</h1>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {config?.accounts.length || 0} {t('paginas')} | {totalAllContacts.toLocaleString()} {t('contatos')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {config?.lastSync && !syncingContacts && (
              <span className="text-[10px] text-muted-foreground/60">
                {t('Ultima sync')}: {(() => {
                  const diff = Math.round((Date.now() - new Date(config.lastSync).getTime()) / 60000)
                  if (diff < 1) return t('agora')
                  if (diff < 60) return `${t('ha')} ${diff}min`
                  if (diff < 1440) return `${t('ha')} ${Math.floor(diff / 60)}h`
                  return new Date(config.lastSync).toLocaleDateString(language === 'en' ? 'en-US' : 'pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                })()}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleSyncAllContacts}
              disabled={syncingContacts}
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${syncingContacts ? 'animate-spin' : ''}`} />
              {syncingContacts ? t('Sincronizando...') : t('Sincronizar Contatos')}
            </Button>
          </div>
        </div>
        {syncStatus && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-2 rounded-lg bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              {syncingContacts && <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />}
              <span className="font-medium">{syncStatus}</span>
              {syncProgress.total > 0 && (
                <span className="ml-auto tabular-nums">{syncProgress.completed}/{syncProgress.total}</span>
              )}
            </div>
            {syncProgress.total > 0 && (
              <div className="w-full h-1.5 bg-muted/40 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.round((syncProgress.completed / syncProgress.total) * 100)}%`,
                    background: 'linear-gradient(90deg, var(--color-primary), #3b82f6)',
                  }}
                />
              </div>
            )}
            {syncProgress.pages.length > 0 && (
              <div className="space-y-0.5 text-[10px] text-muted-foreground/70 px-1">
                {syncProgress.pages.map((p, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <CheckCircle className="h-2.5 w-2.5 text-emerald-500 flex-shrink-0" />
                    {p}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ====== EXPIRED TOKENS ALERT ====== */}
      <ExpiredTokensAlert t={t} />

      {/* ====== MAIN CONTENT: Left panel + Center ====== */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">

        {/* ====== LEFT PANEL: Pages Selection (unchanged) ====== */}
        <div className="w-full lg:w-[320px] shrink-0 border-r border-border/20 overflow-y-auto">
          <div className="bg-card/50 backdrop-blur-sm h-full">
            {/* Panel header */}
            <div className="px-4 py-3 border-b border-border/30 bg-muted/10">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Radio className="h-3.5 w-3.5 text-primary" />
                  <span className="text-sm font-semibold">{t('Paginas')}</span>
                  {selectedAccounts.length > 0 && (
                    <Badge variant="default" className="text-[9px] px-1.5 rounded-md">
                      {selectedAccounts.length}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <Input
                  value={pageSearch}
                  onChange={(e) => setPageSearch(e.target.value)}
                  placeholder={t('Buscar pagina...')}
                  className="h-7 text-[11px] pl-7 rounded-lg border-border/40 bg-muted/20"
                />
              </div>
            </div>

            {/* Select all + filters */}
            <div className="px-4 py-2 border-b border-border/20 space-y-2">
              {filteredAccounts.length > 1 && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={filteredAccounts.length > 0 && filteredAccounts.every(a => selectedAccounts.includes(a.igUserId))}
                    onCheckedChange={() => {
                      const ids = filteredAccounts.map(a => a.igUserId)
                      const allSelected = ids.every(id => selectedAccounts.includes(id))
                      setSelectedAccounts(allSelected ? selectedAccounts.filter(id => !ids.includes(id)) : [...new Set([...selectedAccounts, ...ids])])
                    }}
                  />
                  <span className="text-[11px] font-semibold">{t('Selecionar todas')}</span>
                  <Badge variant="secondary" className="ml-auto text-[9px] rounded-md">
                    {filteredAccounts.reduce((s, a) => s + (isUtilityMode ? (a.totalWithDead || a.totalContacts) : a.totalContacts), 0).toLocaleString()}
                  </Badge>
                </label>
              )}
              <div className="flex items-center gap-3">
                {config?.accounts.some(a => a.igLogin) && (
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <Switch checked={showIgAccounts} onCheckedChange={setShowIgAccounts} className="scale-75" />
                    <span className="text-[10px] text-muted-foreground">{t('Instagram')}</span>
                  </label>
                )}
                {config?.accounts.some(a => a.recentFail) && (
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <Switch checked={hideFailedAccounts} onCheckedChange={setHideFailedAccounts} className="scale-75" />
                    <span className="text-[10px] text-muted-foreground">{t('Ocultar bloqueadas')}</span>
                  </label>
                )}
              </div>
              {/* Profile selector */}
              {config?.profiles && config.profiles.length > 0 && (
                <Select value="" onValueChange={(profileId) => {
                  const profile = config.profiles?.find(p => p.id === profileId)
                  if (!profile) return
                  const matchingIds = config.accounts
                    .filter(a => profile.pageIds.includes(a.pageId))
                    .map(a => a.igUserId)
                  if (matchingIds.length > 0) setSelectedAccounts(matchingIds)
                }}>
                  <SelectTrigger className="h-7 text-[10px] rounded-lg border-border/40">
                    <SelectValue placeholder={t('Selecionar por perfil...')} />
                  </SelectTrigger>
                  <SelectContent>
                    {config.profiles.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} ({p.pageIds.length} {t('paginas')})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Pages list */}
            <div className="overflow-y-auto p-2 space-y-1" style={{ maxHeight: 'calc(100vh - 320px)' }}>
              {filteredAccounts.map((a) => {
                const isSelected = selectedAccounts.includes(a.igUserId)
                return (
                  <label
                    key={a.igUserId}
                    className={cn(
                      'flex items-center gap-2.5 rounded-lg px-3 py-2.5 cursor-pointer transition-all',
                      isSelected ? 'bg-primary/8 border border-primary/20' : 'hover:bg-muted/40 border border-transparent',
                      a.recentFail && 'opacity-60'
                    )}
                  >
                    <Checkbox checked={isSelected} onCheckedChange={() => toggleAccount(a.igUserId)} />
                    <img
                      src={`/api/v2/page-picture/${a.pageId}`}
                      alt=""
                      className="h-[30px] w-[30px] rounded-lg ring-1 ring-border/40 object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-medium truncate">{a.name}</div>
                      <div className="text-[9px] text-muted-foreground tabular-nums">
                        {t('Contatos')}: {(isUtilityMode ? (a.totalWithDead || a.totalContacts) : a.totalContacts).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {a.igLogin && <Badge variant="outline" className="text-[8px] px-1 py-0 rounded border-purple-500/30 text-purple-400">IG</Badge>}
                      {a.recentFail && <Badge variant="outline" className="text-[8px] px-1 py-0 rounded border-amber-500/30 text-amber-400">Rate limit</Badge>}
                    </div>
                  </label>
                )
              })}
              {config?.accounts.some(a => a.recentFail) && hideFailedAccounts && (
                <div className="text-[10px] text-amber-500/60 text-center py-1">
                  {config.accounts.filter(a => a.recentFail).length} {t('conta(s) com rate limit oculta(s)')}
                </div>
              )}
              {filteredAccounts.length === 0 && (
                <div className="text-[11px] text-muted-foreground text-center py-8">
                  {pageSearch ? t('Nenhuma pagina encontrada') : t('Nenhuma pagina disponivel')}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ====== CENTER PANEL ====== */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

          {/* Tabs header */}
          <div className="flex border-b border-border/20 bg-muted/5">
            {([
              { id: 'campanha' as const, label: t('Campanha') },
              { id: 'escrever' as const, label: t('Agendar') },
              { id: 'templates' as const, label: t('Templates') },
              { id: 'historico' as const, label: t('Historico') },
            ]).map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'px-5 py-3 text-xs font-medium border-b-[1.5px] transition-all',
                  activeTab === tab.id
                    ? 'text-foreground border-primary'
                    : 'text-muted-foreground border-transparent hover:text-foreground'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab content area */}
          <div className="flex-1 overflow-y-auto p-6">

            {/* ================================================================
               TAB 1: PRESETS
               ================================================================ */}
            {activeTab === 'presets' && (
              <div className="space-y-6">
                {/* Section: Seus presets */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-[11px] font-semibold text-muted-foreground/60 uppercase tracking-wider">{t('Seus presets')}</h3>
                    <button
                      type="button"
                      onClick={() => { setEditingPreset(null); setPresetModalOpen(true) }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-border/40 text-[10px] text-muted-foreground hover:border-primary/40 hover:text-primary transition-all"
                    >
                      <Plus className="h-3 w-3" /> {t('Novo')}
                    </button>
                  </div>

                  {presetsLoading ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {[1, 2, 3].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}
                    </div>
                  ) : userPresets.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-border/40 p-8 text-center">
                      <p className="text-xs text-muted-foreground">{t('Nenhum preset criado ainda')}</p>
                      <p className="text-[10px] text-muted-foreground/60 mt-1">{t('Crie presets para disparar em 2 cliques')}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {userPresets.map(preset => (
                        <PresetCard
                          key={preset.id}
                          preset={preset}
                          isSelected={selectedPresetId === preset.id}
                          onClick={() => setSelectedPresetId(prev => prev === preset.id ? null : preset.id)}
                          onEdit={() => { setEditingPreset(preset); setPresetModalOpen(true) }}
                          t={t}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Divider */}
                {globalPresets.length > 0 && (
                  <>
                    <div className="h-px bg-border/20" />

                    {/* Section: Presets globais */}
                    <div>
                      <h3 className="text-[11px] font-semibold text-muted-foreground/60 uppercase tracking-wider mb-3">{t('Presets globais')}</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {globalPresets.map(preset => (
                          <PresetCard
                            key={preset.id}
                            preset={preset}
                            isSelected={selectedPresetId === preset.id}
                            onClick={() => setSelectedPresetId(prev => prev === preset.id ? null : preset.id)}
                            onEdit={() => { setEditingPreset(preset); setPresetModalOpen(true) }}
                            t={t}
                            isGlobal
                          />
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ================================================================
               TAB 2: ESCREVER (compose form — same as existing)
               ================================================================ */}
            {activeTab === 'escrever' && (
              <div className="space-y-5 max-w-2xl">
                {/* Scheduling — FIRST item */}
                <div className="rounded-2xl border border-border/40 bg-card/50 backdrop-blur-sm p-5 shadow-sm space-y-4">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-primary/20 to-primary/5">
                      <CalendarClock className="h-3.5 w-3.5 text-primary" />
                    </div>
                    <span className="text-sm font-semibold">{t('Agendamento')}</span>
                    <div className="flex-1" />
                    <span className="text-[10px] text-muted-foreground">{t('Envio por template · todos os contatos')}</span>
                  </div>

                  {true && (
                    <div className="space-y-3">
                      {/* Schedule type toggle */}
                      <div className="flex gap-2">
                        <button type="button" onClick={() => { setScheduleType('once'); setRepeatEnabled(false) }}
                          className={cn('flex-1 py-2 text-xs rounded-lg border transition-colors', scheduleType === 'once' ? 'bg-primary/10 border-primary/30 text-primary font-semibold' : 'border-border/40 text-muted-foreground')}>
                          {t('Unico')}
                        </button>
                        <button type="button" onClick={() => { setScheduleType('recurring'); setRepeatEnabled(true) }}
                          className={cn('flex-1 py-2 text-xs rounded-lg border transition-colors', scheduleType === 'recurring' ? 'bg-primary/10 border-primary/30 text-primary font-semibold' : 'border-border/40 text-muted-foreground')}>
                          {t('Recorrente')}
                        </button>
                      </div>

                      {/* Date + Time */}
                      {scheduleType === 'once' ? (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Data')}</Label>
                            <Input type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} min={new Date().toISOString().split('T')[0]} className="h-9 text-xs rounded-lg border-border/40" />
                          </div>
                          <div>
                            <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Hora')}</Label>
                            <Input type="time" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)} className="h-9 text-xs rounded-lg border-border/40" />
                          </div>
                        </div>
                      ) : (
                        <div>
                          <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                            {t('Horarios')} <span className="text-primary normal-case">({scheduledTimes.length})</span>
                          </Label>
                          <div className="space-y-1.5">
                            {scheduledTimes.map((time, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <Input type="time" value={time} onChange={(e) => updateTime(i, e.target.value)} className="h-9 text-xs rounded-lg border-border/40 flex-1" />
                                {scheduledTimes.length > 1 && (
                                  <button type="button" onClick={() => removeTime(i)} className="h-6 w-6 flex items-center justify-center rounded text-muted-foreground/40 hover:text-destructive transition-colors">
                                    <X className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                            ))}
                            <button type="button" onClick={addTime} className="text-[10px] text-primary hover:underline">+ {t('Adicionar horario')}</button>
                          </div>
                        </div>
                      )}

                      {/* Days of week — recurring only */}
                      {scheduleType === 'recurring' && (
                        <>
                          <div>
                            <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Dias da semana')}</Label>
                            <div className="flex gap-1">
                              {dayLabels.map((label, i) => (
                                <button key={i} type="button" onClick={() => toggleDay(i)}
                                  className={cn('flex-1 h-8 rounded-md text-[10px] font-bold transition-colors',
                                    selectedDays.includes(i) ? 'bg-primary text-primary-foreground' : 'border border-border/40 text-muted-foreground/40 hover:border-primary/30')}>
                                  {label}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Repetir ate (opcional)')}</Label>
                            <Input type="date" value={repeatUntil} onChange={(e) => setRepeatUntil(e.target.value)} min={scheduledDate || new Date().toISOString().split('T')[0]} className="h-9 text-xs rounded-lg border-border/40 max-w-[200px]" />
                            <p className="text-[10px] text-muted-foreground mt-1">{t('Deixe em branco para repetir indefinidamente')}</p>
                          </div>
                        </>
                      )}

                      {/* Template selector — required (fura a janela de 24h) */}
                      <div>
                        <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                          {t('Template aprovado')}
                        </Label>
                        {bcTemplates && bcTemplates.length > 0 ? (
                          <Select value={scheduleTemplateName} onValueChange={setScheduleTemplateName}>
                            <SelectTrigger className="h-9 text-xs rounded-lg border-border/40">
                              <SelectValue placeholder={t('Escolha um template')} />
                            </SelectTrigger>
                            <SelectContent>
                              {bcTemplates.map((tpl: any) => (
                                <SelectItem key={tpl.id} value={tpl.name}>
                                  {tpl.name} <span className="text-muted-foreground">({tpl.approved} {t('pags')})</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="rounded-lg border border-border/40 p-3 text-center">
                            <p className="text-[10px] text-muted-foreground">{t('Crie e aprove um template na aba Templates primeiro')}</p>
                          </div>
                        )}
                        <p className="text-[10px] text-muted-foreground mt-1.5">{t('O template dispara mesmo fora da janela de 24h. As variaveis saem do proprio template.')}</p>
                      </div>

                      {/* Save schedule button */}
                      <button type="button" onClick={() => {
                        const data: any = {
                          schedule_type: scheduleType,
                          account_ids: selectedAccounts.length > 0 ? selectedAccounts : null,
                          filter: 'all',
                          filter_tag: null,
                          template_name: scheduleTemplateName || undefined,
                          time_of_day: scheduleType === 'once' ? (scheduledTime || '10:00') : (scheduledTimes.filter(t => t)[0] || '00:10'),
                          times_of_day: scheduleType === 'recurring' ? scheduledTimes.filter(t => t) : null,
                          scheduled_at: null,
                          days_of_week: scheduleType === 'recurring' ? selectedDays : null,
                          ends_at: null,
                        }
                        if (scheduleType === 'once') {
                          data.scheduled_at = scheduledDate && scheduledTime ? new Date(`${scheduledDate}T${scheduledTime}`).toISOString() : null
                        } else if (repeatUntil) {
                          data.ends_at = new Date(`${repeatUntil}T23:59:59`).toISOString()
                        }
                        const done = () => {
                          setScheduleEnabled(false)
                          setScheduleTemplateName('')
                          setEditingScheduleId(null)
                          alert(editingScheduleId ? t('Agendamento atualizado!') : t('Agendamento salvo!'))
                        }
                        if (editingScheduleId) {
                          updateSchedule.mutate({ id: editingScheduleId, data }, { onSuccess: done })
                        } else {
                          createSchedule.mutate(data, { onSuccess: done })
                        }
                      }} disabled={!scheduleTemplateName || createSchedule.isPending || updateSchedule.isPending}
                        className={cn('w-full py-2.5 rounded-lg text-xs font-semibold transition-opacity',
                          scheduleTemplateName ? 'bg-primary text-primary-foreground hover:opacity-90' : 'bg-muted/30 text-muted-foreground cursor-not-allowed')}>
                        {(createSchedule.isPending || updateSchedule.isPending) ? '...' : !scheduleTemplateName ? t('Selecione um template') : editingScheduleId ? t('Atualizar Agendamento') : t('Salvar Agendamento')}
                      </button>
                      {editingScheduleId && (
                        <button type="button" onClick={() => { setEditingScheduleId(null); setScheduleEnabled(false); setScheduleTemplateName('') }}
                          className="w-full py-2 rounded-lg text-[11px] text-muted-foreground hover:text-foreground transition-colors">
                          {t('Cancelar edicao')}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Compositor de texto livre removido — agendamento agora e sempre por template */}
                {false && (<>
                {/* Reach + Filter */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Alcance (somente FB)')}</Label>
                    <Select value={messageTag} onValueChange={setMessageTag}>
                      <SelectTrigger className="h-9 text-xs rounded-lg border-border/40"><SelectValue placeholder={t('Padrao (janela de 24h)')} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t('Normal (janela de 24h)')}</SelectItem>
                        <SelectItem value="UTILITY">{t('Alcance Maximo')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Filtro de audiencia')}</Label>
                    <Select value={filter} onValueChange={(v) => setFilter(v as 'all' | '24h' | 'tag')}>
                      <SelectTrigger className="h-9 text-xs rounded-lg border-border/40"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('Todos os contatos')}</SelectItem>
                        <SelectItem value="24h">{t('Apenas dentro de 24h')}</SelectItem>
                        <SelectItem value="tag">{t('Por tag')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {filter === 'tag' && (
                  <div>
                    <Label className="mb-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Nome da tag')}</Label>
                    <Input value={filterTag} onChange={(e) => setFilterTag(e.target.value)} placeholder="Ex: vip, importado, promo" className="h-9 text-xs rounded-lg border-border/40 bg-muted/30 border-transparent focus:border-primary/30 focus:bg-transparent transition-colors" />
                  </div>
                )}

                {/* Text / Card toggle */}
                <div className="flex gap-1 p-1 rounded-lg bg-muted/30 w-fit">
                  <button type="button" onClick={() => setMessageMode('text')} className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${messageMode === 'text' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>{t('Texto')}</button>
                  <button type="button" onClick={() => { setMessageMode('card'); if (buttons.length === 0) setButtons([{ text: '', type: 'url', value: '' }]) }} className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${messageMode === 'card' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>Card</button>
                </div>

                {/* Message area */}
                <div className="rounded-2xl border border-border/40 bg-card/50 backdrop-blur-sm p-5 shadow-sm space-y-5">
                  {messageMode === 'text' ? (
                    <div>
                      <Label className="mb-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Texto da mensagem')}</Label>
                      <FormattingToolbar textareaRef={messageTextareaRef} value={message} onChange={setMessage} />
                      <Textarea
                        ref={messageTextareaRef}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder={t('Ola {{nome}}, tudo bem? Confira nossa *promocao*!')}
                        className="min-h-[120px] resize-none text-sm rounded-xl border-border/40 bg-muted/20 focus:bg-transparent transition-colors"
                      />
                      <VariableChips onInsert={v => setMessage(prev => prev + v)} />
                      {imageUrl && message.length > 80 && (
                        <div className="text-[10px] text-amber-500 mt-1">
                          {t('Com imagem, titulo fica limitado a 80 chars')} ({message.length}/80) — {t('negrito pode ser cortado')}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 text-[11px] text-muted-foreground">
                        Card com imagem clicavel, titulo, subtitulo e botoes. Funciona no Facebook Messenger. No Instagram, envia como texto com links.
                      </div>
                      <div>
                        <Label className="mb-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Titulo *</Label>
                        <Input value={cardTitle} onChange={e => setCardTitle(e.target.value)} placeholder="Promocao especial!" maxLength={80} className="h-9 text-sm rounded-lg border-border/40" />
                        <span className="text-[9px] text-muted-foreground">{cardTitle.length}/80</span>
                      </div>
                      <div>
                        <Label className="mb-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Subtitulo</Label>
                        <Input value={cardSubtitle} onChange={e => setCardSubtitle(e.target.value)} placeholder="So hoje com 50% off" maxLength={80} className="h-9 text-sm rounded-lg border-border/40" />
                        <span className="text-[9px] text-muted-foreground">{cardSubtitle.length}/80</span>
                      </div>
                      <div className="text-[10px] text-muted-foreground">A imagem do card e o botao de link sao configurados abaixo. A imagem sera clicavel e abrira o link do 1o botao.</div>
                    </div>
                  )}

                  {/* Image */}
                  <div>
                    <div className="mb-2.5 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Imagem (opcional)')}</Label>
                        {!imagePreview && (
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <Checkbox checked={antiFingerprint} onCheckedChange={(v) => setAntiFingerprint(!!v)} />
                            <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Shield className="h-3 w-3" /> Anti-RM</span>
                          </label>
                        )}
                      </div>
                      {!imagePreview && (
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <span className="text-[10px] text-muted-foreground">{t('Filtros:')}</span>
                          {[
                            { key: 'player', label: 'Play', icon: <Play className="h-2.5 w-2.5" /> },
                            { key: 'blur', label: 'Blur' },
                            { key: 'click', label: 'Clique' },
                            { key: 'sensitive', label: 'Sensivel', icon: <EyeOff className="h-2.5 w-2.5" /> },
                          ].map(f => (
                            <label key={f.key} className="flex items-center gap-1 cursor-pointer">
                              <Checkbox checked={filters.includes(f.key)} onCheckedChange={() => toggleFilter(f.key)} />
                              <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">{f.icon}{f.label}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleUpload} className="hidden" />
                    {imagePreview ? (
                      <div className="relative inline-block">
                        <img src={imagePreview} alt="Preview" className="h-24 w-24 rounded-xl object-cover border border-border/40" />
                        {(filters.includes('blur') || filters.includes('sensitive')) && <div className="absolute inset-0 h-24 w-24 rounded-xl backdrop-blur-sm pointer-events-none" />}
                        {filters.includes('sensitive') && <div className="absolute inset-0 h-24 w-24 rounded-xl bg-black/45 pointer-events-none" />}
                        {filters.includes('player') && !filters.includes('sensitive') && <div className="absolute inset-0 h-24 w-24 rounded-xl bg-black/18 pointer-events-none" />}
                        {filters.includes('player') && (
                          <div className="absolute inset-0 h-24 w-24 flex items-center justify-center pointer-events-none">
                            <Play className="h-14 w-14 text-white fill-white drop-shadow-lg" />
                          </div>
                        )}
                        {filters.includes('sensitive') && (
                          <div className="absolute inset-0 h-24 w-24 flex items-center justify-center pointer-events-none">
                            <EyeOff className="h-8 w-8 text-white/70" />
                          </div>
                        )}
                        {filters.includes('click') && (
                          <div className="absolute inset-0 h-24 w-24 flex items-center justify-start pl-1 pointer-events-none">
                            <svg viewBox="0 0 90 90" className="h-10 w-10 drop-shadow-lg" fill="white" opacity="0.9"><path d="M66.4 24.9c.4-1.9 0-3.8-1.1-5.3-1.1-1.5-2.8-2.5-4.7-2.7-1.9-.2-3.9.3-5.5 1.5L22.5 42.2l.2-8.5c0-1.9-.7-3.6-2-4.9-1.4-1.3-3.3-2-5.3-1.9-4 .2-7.3 3.6-7.4 7.5L7.4 65.1c-.1 5.5 1.5 10.7 4.6 14.9 4.8 6.6 12.4 10 20.4 10 5.9 0 11.9-1.9 17.2-5.7l13.7-10c3.3-2.5 4.2-7 1.9-10.1-1.1-1.5-2.8-2.5-4.7-2.7l.4-.3c1.6-1.2 2.7-2.9 3.1-4.8.4-1.9 0-3.8-1.1-5.3-1.1-1.5-2.8-2.5-4.7-2.7-.4 0-.7 0-1.1 0 .4-.7.8-1.6.9-2.4.4-1.9 0-3.8-1.1-5.3-1.2-1.6-2.9-2.5-4.8-2.7l11.5-8.4c1.6-1.2 2.7-2.9 3.1-4.8z"/><path d="M53 14.9l-6.4-9.2c-.3-.5-.2-1.1.2-1.4.5-.3 1.1-.2 1.4.2l6.4 9.2c.3.5.2 1.1-.2 1.4z"/><path d="M72.3 42.6l-6.4-9.2c-.3-.5-.2-1.1.2-1.4.5-.3 1.1-.2 1.4.2l6.4 9.2c.3.5.2 1.1-.2 1.4z"/><path d="M70.4 17.1c-.3-.5-.2-1.1.2-1.4l9.2-6.4c.5-.3 1.1-.2 1.4.2.3.5.2 1.1-.2 1.4l-9.2 6.4z"/></svg>
                          </div>
                        )}
                        <button type="button" onClick={() => { setImageUrl(''); setImagePreview('') }} className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow-sm">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="flex h-20 w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border/40 text-xs text-muted-foreground transition-all duration-200 hover:border-primary/40 hover:bg-primary/5">
                        {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> {t('Enviando...')}</> : <><ImagePlus className="h-5 w-5" /> {t('Clique para enviar uma imagem')}</>}
                      </button>
                    )}
                  </div>

                  {/* Buttons */}
                  <div>
                    <div className="mb-2.5 flex items-center justify-between">
                      <Label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5"><Link className="h-3 w-3" /> {messageMode === 'card' ? t('Botoes (max 2, 1o obrigatorio)') : t('Botoes (opcional, max 3)')}</Label>
                      {buttons.length < (messageMode === 'card' ? 2 : 3) && (
                        <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2 gap-1 rounded-lg hover:bg-primary/10 hover:text-primary transition-colors" onClick={addButton}>{t('+ Adicionar botao')}</Button>
                      )}
                    </div>
                    <div className="space-y-2">
                      {buttons.map((btn, idx) => (
                        <div key={idx} className="flex items-start gap-2 rounded-xl border border-border/40 bg-muted/20 p-3 transition-all duration-200">
                          <div className="flex-1 space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                              <Input value={btn.text} onChange={(e) => updateButton(idx, 'text', e.target.value)} placeholder={t('Texto do botao')} className="h-8 text-xs rounded-lg border-border/40" />
                              <Select value={btn.type} onValueChange={(v) => updateButton(idx, 'type', v)}>
                                <SelectTrigger className="h-8 text-xs rounded-lg border-border/40"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="url">Link (URL)</SelectItem>
                                  <SelectItem value="postback">{t('Resposta automatica')}</SelectItem>
                                  <SelectItem value="quick_reply">Quick Reply</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            {btn.type === 'url' ? (
                              <div>
                                <Textarea
                                  value={btn.value}
                                  onChange={(e) => updateButton(idx, 'value', e.target.value)}
                                  placeholder={"Cole uma ou mais URLs (uma por linha para rotacao)\nhttps://wa.me/5511999990001\nhttps://wa.me/5511999990002"}
                                  className="min-h-[60px] resize-none text-xs rounded-lg border-border/40"
                                  rows={3}
                                />
                                {btn.value.includes('\n') && btn.value.split('\n').filter(l => l.trim()).length > 1 && (
                                  <span className="text-[9px] text-primary mt-1 block">
                                    {btn.value.split('\n').filter(l => l.trim()).length} links em rotacao
                                  </span>
                                )}
                              </div>
                            ) : btn.type === 'quick_reply' ? (
                              <div className="text-[10px] text-muted-foreground bg-emerald-500/10 rounded-lg px-2.5 py-1.5">
                                {language === 'en'
                                  ? <>Appears above keyboard. Click <strong className="text-emerald-500">resets 24h window</strong> + triggers keyword flow</>
                                  : <>Aparece acima do teclado. Clique <strong className="text-emerald-500">reseta janela 24h</strong> + dispara fluxo de keyword</>}
                              </div>
                            ) : (
                              <div className="text-[10px] text-muted-foreground bg-muted/30 rounded-lg px-2.5 py-1.5">
                                {language === 'en'
                                  ? <>When clicked, the contact sends "<strong className="text-foreground">{btn.text || '...'}</strong>" automatically</>
                                  : <>Quando clicar, o contato envia "<strong className="text-foreground">{btn.text || '...'}</strong>" automaticamente</>}
                              </div>
                            )}
                          </div>
                          <button type="button" onClick={() => removeButton(idx)} className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                </>)}
              </div>
            )}

            {/* ================================================================
               TAB: TEMPLATES (Meta utility templates p/ fora da janela de 24h)
               ================================================================ */}
            {activeTab === 'templates' && (
              <TemplatesTab
                accounts={(selectedAccountsList.length ? selectedAccountsList : (config?.accounts || []))
                  .map(a => ({ igUserId: a.igUserId, pageId: a.pageId, name: a.name }))}
                t={t}
                onSchedule={openScheduleForTemplate}
              />
            )}

            {/* ================================================================
               TAB 3: HISTORICO
               ================================================================ */}
            {activeTab === 'historico' && (
              <div className="space-y-6">
                {/* Active broadcast progress — always polls, returns null if nothing active */}
                <BroadcastPanel
                  onAllComplete={() => {
                    setActiveBroadcasts([])
                    setBroadcastStats({})
                    setToastShown(false)
                    qc.invalidateQueries({ queryKey: ['broadcast-history'] })
                  }}
                  t={t}
                />

                {/* Scheduled broadcasts list */}
                {schedules && schedules.length > 0 && (
                  <div className="overflow-hidden rounded-2xl border border-border/40 bg-card/50 backdrop-blur-sm shadow-sm">
                    <div className="flex items-center gap-2.5 border-b border-border/30 bg-muted/10 px-5 py-4">
                      <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-primary/20 to-primary/5">
                        <CalendarClock className="h-3.5 w-3.5 text-primary" />
                      </div>
                      <h2 className="text-sm font-semibold">{t('Agendamentos')}</h2>
                      <span className="text-[10px] px-2 py-0.5 rounded-md bg-primary/10 text-primary font-semibold">{schedules.length}</span>
                      <div className="flex-1" />
                      <button type="button" onClick={() => { if (confirm(t('Deletar TODOS os agendamentos?'))) clearSchedules.mutate() }}
                        className="text-[9px] px-2.5 py-1 rounded-md bg-destructive/10 text-destructive hover:bg-destructive/20 font-semibold transition-colors">
                        {t('Limpar todos')}
                      </button>
                    </div>
                    <div className="divide-y divide-border/20">
                      {schedules.map((s: any) => {
                        const dowNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab']
                        const daysLabel = s.scheduleType === 'recurring' && s.daysOfWeek
                          ? (s.daysOfWeek.length === 7 ? 'Diario' : s.daysOfWeek.map((d: number) => dowNames[d]).join(' '))
                          : 'Unico'
                        const nextLabel = s.nextRun ? new Date(s.nextRun).toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : null
                        return (
                          <div key={s.id} className={cn('flex items-center gap-3 px-5 py-3', !s.isActive && 'opacity-40')}>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-semibold truncate">{s.templateName || (s.presetNames?.length > 1 ? `${s.presetNames.length} presets` : s.presetName) || s.message?.substring(0, 40) || s.title || t('Sem nome')}</div>
                              <div className="text-[10px] text-muted-foreground mt-0.5">{s.accountIds ? `${s.accountIds.length} contas` : t('Todas as contas')}</div>
                            </div>
                            <div className="flex gap-1.5 flex-shrink-0">
                              <span className="text-[8px] bg-primary/10 text-primary px-1.5 py-0.5 rounded font-medium">{daysLabel}</span>
                              {(s.timesOfDay?.length > 0 ? s.timesOfDay : s.timeOfDay ? [s.timeOfDay] : []).map((t: string, ti: number) => (
                                <span key={ti} className="text-[8px] bg-indigo-500/10 text-indigo-400 px-1.5 py-0.5 rounded font-medium">{t}</span>
                              ))}
                              {nextLabel && <span className="text-[8px] bg-emerald-500/10 text-emerald-500 px-1.5 py-0.5 rounded font-medium">{nextLabel}</span>}
                              {s.endsAt && <span className="text-[8px] bg-amber-500/10 text-amber-500 px-1.5 py-0.5 rounded font-medium">{t('Ate')} {new Date(s.endsAt).toLocaleDateString('pt-BR')}</span>}
                            </div>
                            <button type="button" onClick={() => loadScheduleForEdit(s)}
                              title={t('Editar')}
                              className="text-muted-foreground/40 hover:text-primary transition-colors flex-shrink-0">
                              <Pencil className="h-3 w-3" />
                            </button>
                            <button type="button" onClick={() => toggleSchedule.mutate(s.id)}
                              className={cn('w-8 h-[18px] rounded-full relative transition-colors flex-shrink-0 cursor-pointer', s.isActive ? 'bg-primary' : 'bg-muted/40')}>
                              <div className={cn('w-3.5 h-3.5 rounded-full bg-white absolute top-[1px] transition-all', s.isActive ? 'right-[2px]' : 'left-[2px]')} />
                            </button>
                            <button type="button" onClick={() => { if (confirm(t('Deletar agendamento?'))) deleteSchedule.mutate(s.id) }}
                              className="text-muted-foreground/40 hover:text-destructive transition-colors flex-shrink-0">
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* History list */}
                <div className="overflow-hidden rounded-2xl border border-border/40 bg-card/50 backdrop-blur-sm shadow-sm">
                  <div className="flex items-center gap-2.5 border-b border-border/30 bg-muted/10 px-5 py-4">
                    <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-primary/20 to-primary/5">
                      <History className="h-3.5 w-3.5 text-primary" />
                    </div>
                    <h2 className="text-sm font-semibold">{t('Historico de Broadcasts')}</h2>
                    {historyData && historyData.length > 0 && <Badge variant="secondary" className="text-[10px] rounded-md tabular-nums">{historyData.length}</Badge>}
                    <div className="flex-1" />
                    {historyData && historyData.length > 0 && (
                      <Button
                        variant="ghost" size="sm"
                        className="h-7 gap-1 text-xs text-destructive hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                        disabled={clearHistory.isPending}
                        onClick={() => {
                          if (confirm(t('Apagar todo o historico de broadcasts? Essa acao nao pode ser desfeita.'))) {
                            clearHistory.mutate(undefined, {
                              onSuccess: () => toast.success(t('Historico apagado')),
                              onError: () => toast.error(t('Erro ao apagar historico')),
                            })
                          }
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                        {clearHistory.isPending ? t('Apagando...') : t('Limpar historico')}
                      </Button>
                    )}
                  </div>

                  {!historyData || historyData.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted/30 mb-3">
                        <History className="h-6 w-6 text-muted-foreground/40" />
                      </div>
                      <p className="text-xs text-muted-foreground">{t('Nenhum broadcast enviado ainda')}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{t('Os broadcasts enviados aparecerão aqui')}</p>
                    </div>
                  ) : (() => {
                    const grouped: { key: string; records: typeof historyData }[] = []
                    const batchMap = new Map<string, typeof historyData>()
                    for (const r of historyData) {
                      const bk = r.batchId || r.id
                      if (!batchMap.has(bk)) batchMap.set(bk, [])
                      batchMap.get(bk)!.push(r)
                    }
                    batchMap.forEach((records, key) => grouped.push({ key, records }))

                    const totalGroups = grouped.length
                    const totalGroupPages = Math.ceil(totalGroups / historyPageSize)
                    const pagedGroups = grouped.slice((historyPage - 1) * historyPageSize, historyPage * historyPageSize)

                    return (
                      <>
                        <div className="divide-y divide-border/20">
                          {pagedGroups.map(({ key, records }) => {
                            const first = records[0]
                            const date = new Date(first.createdAt)
                            const dateStr = date.toLocaleDateString(dateFmtLocale, { day: '2-digit', month: '2-digit', year: '2-digit' })
                            const timeStr = date.toLocaleTimeString(dateFmtLocale, { hour: '2-digit', minute: '2-digit' })
                            const isExpanded = expandedId === key
                            const allAccounts = records.flatMap(r => r.accounts)
                            const totalSentBatch = records.reduce((s, r) => s + r.sent, 0)
                            const totalFailedBatch = records.reduce((s, r) => s + r.failed, 0)
                            const totalContactsBatch = records.reduce((s, r) => s + r.totalContacts, 0)
                            const allFailed = totalSentBatch === 0 && totalFailedBatch > 0
                            const isScheduled = first.status === 'scheduled'
                            const hasFlow = first.flowSteps && first.flowSteps.length > 1
                            const isMultiPage = allAccounts.length > 1
                            const allCompleted = records.every(r => r.status === 'completed' || r.status === 'failed')
                            const someSending = records.some(r => r.status === 'sending' || r.status === 'syncing' || r.status === 'pending')
                            const successRate = totalContactsBatch > 0 ? Math.round((totalSentBatch / totalContactsBatch) * 100) : 0

                            return (
                              <div key={key} className="group transition-colors hover:bg-muted/10">
                                <button
                                  type="button"
                                  onClick={() => setExpandedId(isExpanded ? null : key)}
                                  className="w-full text-left px-5 py-4"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="shrink-0 w-[80px]">
                                      <div className="text-[11px] font-medium tabular-nums">{dateStr}</div>
                                      <div className="text-[10px] text-muted-foreground tabular-nums">{timeStr}</div>
                                    </div>

                                    <div className="shrink-0">
                                      {isScheduled ? (
                                        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-500/10">
                                          <CalendarClock className="h-3.5 w-3.5 text-amber-500" />
                                        </div>
                                      ) : someSending ? (
                                        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
                                          <Loader2 className="h-3.5 w-3.5 text-primary animate-spin" />
                                        </div>
                                      ) : allFailed ? (
                                        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-destructive/10">
                                          <XCircle className="h-3.5 w-3.5 text-destructive" />
                                        </div>
                                      ) : (
                                        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-500/10">
                                          <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                                        </div>
                                      )}
                                    </div>

                                    <div className="flex-1 min-w-0">
                                      <div className="text-[11px] font-medium truncate">
                                        {hasFlow ? `[${first.flowSteps!.length} ${t('etapas')}] ` : ''}{first.message}
                                      </div>
                                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                        {isMultiPage && (
                                          <Badge variant="secondary" className="text-[9px] px-1.5 py-0 rounded-md gap-0.5">
                                            <Layers className="h-2.5 w-2.5" /> {allAccounts.length} {t('paginas')}
                                          </Badge>
                                        )}
                                        {allAccounts.slice(0, isExpanded ? allAccounts.length : 3).map((name, i) => (
                                          <Badge key={i} variant="outline" className="text-[9px] px-1.5 py-0 rounded-md border-border/40">{name}</Badge>
                                        ))}
                                        {!isExpanded && allAccounts.length > 3 && (
                                          <span className="text-[9px] text-muted-foreground">+{allAccounts.length - 3}</span>
                                        )}
                                      </div>
                                    </div>

                                    <div className="flex items-center gap-3 shrink-0">
                                      {isScheduled ? (
                                        <Badge variant="outline" className="text-[10px] rounded-md border-amber-500/40 text-amber-600 dark:text-amber-400">{t('Agendado')}</Badge>
                                      ) : (
                                        <>
                                          <div className="text-right">
                                            <div className={`text-sm font-bold tabular-nums ${allFailed ? 'text-destructive' : 'text-emerald-500'}`}>{totalSentBatch.toLocaleString()}</div>
                                            <div className="text-[9px] text-muted-foreground">{t('enviados')}</div>
                                          </div>
                                          {totalFailedBatch > 0 && (
                                            <div className="text-right">
                                              <div className="text-sm font-bold tabular-nums text-amber-500">{totalFailedBatch.toLocaleString()}</div>
                                              <div className="text-[9px] text-amber-500/60">{t('leads inativos')}</div>
                                            </div>
                                          )}
                                          <div className="text-right">
                                            <div className="text-sm font-bold tabular-nums text-muted-foreground">{totalContactsBatch.toLocaleString()}</div>
                                            <div className="text-[9px] text-muted-foreground">{t('Total')}</div>
                                          </div>
                                          {!isScheduled && allCompleted && (
                                            <span className="text-sm font-bold tabular-nums text-foreground">{successRate}%</span>
                                          )}
                                        </>
                                      )}
                                      {isExpanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                                    </div>
                                  </div>

                                  {(first.repeatType || first.messageTag || first.imageUrl) && (
                                    <div className="flex gap-1.5 mt-2 ml-[92px]">
                                      {first.repeatType && (
                                        <Badge variant="outline" className="text-[8px] px-1 py-0 rounded border-blue-500/40 text-blue-600 dark:text-blue-400 gap-0.5">
                                          <Repeat className="h-2.5 w-2.5" /> {t('Diario')}
                                        </Badge>
                                      )}
                                      {first.messageTag && (
                                        <Badge variant="outline" className="text-[9px] px-1.5 rounded-md border-border/40">{first.messageTag.replace(/_/g, ' ')}</Badge>
                                      )}
                                      {first.imageUrl && (
                                        <Badge variant="outline" className="text-[9px] px-1.5 rounded-md border-border/40 gap-0.5">
                                          <ImagePlus className="h-2.5 w-2.5" /> {t('Com imagem')}
                                        </Badge>
                                      )}
                                    </div>
                                  )}
                                </button>

                                {/* Expanded detail */}
                                {isExpanded && (
                                  <div className="px-5 pb-4 space-y-3">
                                    <div className="ml-[92px] rounded-xl bg-muted/15 border border-border/30 p-3">
                                      <div className="text-[11px] whitespace-pre-wrap break-words">{first.message}</div>
                                      {first.buttonText && (
                                        <div className="mt-2 flex items-center gap-1 text-[10px] text-muted-foreground">
                                          <Link className="h-3 w-3" />
                                          <span className="font-medium">{first.buttonText}</span>
                                          {first.buttonUrl && <span className="truncate">→ {first.buttonUrl}</span>}
                                        </div>
                                      )}
                                    </div>

                                    {hasFlow && first.flowSteps!.map((fs, fi) => (
                                      <div key={fi} className="ml-[92px] rounded-xl bg-muted/15 border border-border/30 p-3">
                                        <div className="text-[10px] text-primary font-semibold mb-1">{t('Etapa')} {fi + 1}{fi > 0 ? ' (+3h)' : ''}</div>
                                        <div className="text-[11px] whitespace-pre-wrap">{fs.message}</div>
                                        {fs.imageUrl && <div className="text-[9px] text-muted-foreground mt-1 flex items-center gap-0.5"><ImagePlus className="h-2.5 w-2.5" /> {t('Com imagem')}</div>}
                                        {fs.buttons && fs.buttons.length > 0 && fs.buttons.map((b, bi) => (
                                          <div key={bi} className="text-[9px] text-muted-foreground flex items-center gap-0.5 mt-0.5"><Link className="h-2.5 w-2.5" /> {b.text}{b.value ? ` → ${b.value}` : ''}</div>
                                        ))}
                                      </div>
                                    ))}

                                    {totalFailedBatch > 0 && (
                                      <div className="ml-[92px] rounded-lg bg-amber-500/5 border border-amber-500/15 px-3 py-2">
                                        <div className="text-[10px] text-amber-500/80">
                                          <span className="font-semibold">{totalFailedBatch.toLocaleString()} {t('leads inativos')}</span> — {t('contatos que bloquearam a pagina ou desativaram a conta. Ative automacoes de comentarios para gerar novos leads e reativar a base.')}
                                        </div>
                                      </div>
                                    )}

                                    {isMultiPage && (
                                      <div className="ml-[92px]">
                                        <div className="text-[10px] font-semibold text-muted-foreground/50 uppercase tracking-wider mb-2">{t('Detalhes por pagina')}</div>
                                        <div className="space-y-1">
                                          {records.map(r => {
                                            const rPct = r.totalContacts > 0 ? Math.round((r.sent / r.totalContacts) * 100) : 0
                                            return (
                                              <div key={r.id} className="flex items-center gap-2.5 py-1.5 px-3 rounded-lg bg-muted/10">
                                                {r.status === 'completed' ? (
                                                  <CheckCircle className="h-3 w-3 text-emerald-500 shrink-0" />
                                                ) : r.status === 'failed' ? (
                                                  <XCircle className="h-3 w-3 text-destructive shrink-0" />
                                                ) : (
                                                  <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse shrink-0" />
                                                )}
                                                <span className="text-[11px] font-medium text-muted-foreground flex-1 truncate">{r.accounts[0] || '...'}</span>
                                                <span className="text-[10px] text-emerald-500 tabular-nums">{r.sent.toLocaleString()}</span>
                                                {r.failed > 0 && <span className="text-[10px] text-destructive tabular-nums">{r.failed.toLocaleString()}</span>}
                                                <span className="text-[10px] text-muted-foreground/60 tabular-nums">/ {r.totalContacts.toLocaleString()}</span>
                                                <div className="w-16 h-1 bg-muted/20 rounded-full overflow-hidden shrink-0">
                                                  <div className="h-full rounded-full bg-emerald-500/70" style={{ width: `${rPct}%` }} />
                                                </div>
                                                <span className="text-[10px] text-muted-foreground/40 tabular-nums w-7 text-right">{rPct}%</span>
                                              </div>
                                            )
                                          })}
                                        </div>
                                      </div>
                                    )}

                                    <div className="ml-[92px] flex justify-end">
                                      <Button
                                        variant="ghost" size="sm"
                                        className="h-7 gap-1 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          const msg = records.length > 1
                                            ? (language === 'en' ? `Delete all ${records.length} records from this broadcast?` : `Excluir todos os ${records.length} registros deste broadcast?`)
                                            : (language === 'en' ? 'Delete this record from history?' : 'Excluir este registro do historico?')
                                          if (confirm(msg)) {
                                            records.forEach(r => {
                                              deleteBroadcast.mutate(r.id, {
                                                onSuccess: () => {},
                                                onError: () => toast.error(t('Erro ao excluir')),
                                              })
                                            })
                                            toast.success(t('Registro excluido'))
                                          }
                                        }}
                                      >
                                        <Trash2 className="h-3 w-3" />
                                        {t('Excluir')}
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>

                        {totalGroups > historyPageSize && (
                          <div className="flex items-center justify-between border-t border-border/30 bg-muted/10 backdrop-blur-sm px-4 py-2.5">
                            <span className="text-[10px] text-muted-foreground tabular-nums">
                              {(historyPage - 1) * historyPageSize + 1}-{Math.min(historyPage * historyPageSize, totalGroups)} {t('de')} {totalGroups}
                            </span>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" disabled={historyPage <= 1} onClick={() => setHistoryPage((p) => p - 1)}>
                                <ChevronLeft className="h-3.5 w-3.5" />
                              </Button>
                              <span className="text-[10px] text-muted-foreground tabular-nums px-1">{historyPage}/{totalGroupPages}</span>
                              <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" disabled={historyPage >= totalGroupPages} onClick={() => setHistoryPage((p) => p + 1)}>
                                <ChevronRight className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    )
                  })()}
                </div>
              </div>
            )}

            {/* ================================================================
               TAB 4: CAMPANHA
               ================================================================ */}
            {activeTab === 'campanha' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold">{t('Campanhas')}</h3>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{t('Banco rotativo de frases, imagens e botões com disparo automático')}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => openCampaignModal()}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-border/40 text-[10px] text-muted-foreground hover:border-primary/40 hover:text-primary transition-all"
                  >
                    <Plus className="h-3 w-3" /> {t('Nova campanha')}
                  </button>
                </div>

                {/* eCPM Peak Hours Suggestion */}
                <EcpmSuggestionCard
                  onApplyTimes={(times) => {
                    openCampaignModal()
                    setTimeout(() => {
                      setCTimes(times)
                      setCTimeMode('manual')
                    }, 0)
                  }}
                  t={t}
                />

                {campaigns.isLoading ? (
                  <div className="space-y-3">
                    {[1, 2].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}
                  </div>
                ) : !campaigns.data || campaigns.data.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-border/40 p-10 text-center">
                    <Megaphone className="h-8 w-8 text-muted-foreground/20 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">{t('Nenhuma campanha criada')}</p>
                    <p className="text-[11px] text-muted-foreground/60 mt-1">{t('Crie uma campanha para disparar automaticamente com conteúdo rotativo')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {campaigns.data.map(campaign => (
                      <div key={campaign.id} className="rounded-xl border border-border/20 bg-card/50 p-4 space-y-2">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold truncate">{campaign.name}</span>
                              <span className={cn(
                                'text-[9px] px-1.5 py-0.5 rounded font-semibold',
                                campaign.isActive ? 'bg-emerald-500/10 text-emerald-500' : 'bg-muted text-muted-foreground'
                              )}>
                                {campaign.isActive ? t('Ativa') : t('Pausada')}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                              {campaign.subtitles && campaign.subtitles.length > 0 && (
                                <span className="text-[10px] px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-500 font-medium">
                                  Card
                                </span>
                              )}
                              <span className="text-[10px] px-2 py-0.5 rounded-md bg-muted/50 text-muted-foreground">
                                {campaign.phrases.length} {t('títulos')}
                              </span>
                              {campaign.images.length > 0 && (
                                <span className="text-[10px] px-2 py-0.5 rounded-md bg-muted/50 text-muted-foreground">
                                  {campaign.images.length} {t('imagens')}
                                </span>
                              )}
                              <span className="text-[10px] px-2 py-0.5 rounded-md bg-muted/50 text-muted-foreground">
                                {campaign.buttonTexts.length} CTAs
                              </span>
                              <span className="text-[10px] px-2 py-0.5 rounded-md bg-muted/50 text-muted-foreground">
                                {campaign.timesOfDay.length}x/dia
                              </span>
                            </div>
                            <p className="text-[10px] text-muted-foreground/60 mt-1 truncate">
                              {campaign.timesOfDay.slice(0, 5).join(' · ')}{campaign.timesOfDay.length > 5 ? ` +${campaign.timesOfDay.length - 5}` : ''}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Switch
                              checked={campaign.isActive}
                              onCheckedChange={() => toggleCampaign.mutate(campaign.id)}
                              className="scale-75"
                            />
                            <button
                              type="button"
                              onClick={() => openCampaignModal(campaign)}
                              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                if (!confirm(t('Deletar campanha?'))) return
                                await deleteCampaign.mutateAsync(campaign.id)
                                toast.success(t('Campanha deletada'))
                              }}
                              className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                        {campaign.lastRunAt && (
                          <p className="text-[10px] text-muted-foreground/50">
                            {t('Último disparo')}: {new Date(campaign.lastRunAt).toLocaleString('pt-BR')}
                            {' · '}{t('Próximo slot')}: #{(() => { const total = (campaign.presetIds?.length || 0) + campaign.phrases.length; return total > 0 ? `${(campaign.phraseIndex % total) + 1}/${total}` : '—' })()}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ====== BOTTOM BAR (always visible) ====== */}
          <div className="px-6 py-3.5 border-t border-border/30 bg-card/30 backdrop-blur-sm shrink-0">
            <div className="flex items-center justify-between">
              {/* Left: summary */}
              <div className="flex items-center gap-4 text-[11px] text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Radio className="h-3 w-3" />
                  <span className="font-medium">{selectedAccounts.length}</span> {t('paginas')}
                </div>
                <div className="h-3 w-px bg-border/40" />
                <div className="flex items-center gap-1.5">
                  <Users className="h-3 w-3" />
                  <span className="font-medium">{totalSelectedContacts.toLocaleString()}</span> {t('contatos')}
                </div>
                {presetLabel && (
                  <>
                    <div className="h-3 w-px bg-border/40" />
                    <span className="text-foreground font-medium truncate max-w-[180px]">{presetLabel}</span>
                  </>
                )}
              </div>

              {/* Right: reach toggle + send button */}
              <div className="flex items-center gap-3">
                {/* Reach toggle (visible when on presets tab or as override) */}
                <div className="flex gap-0.5 p-0.5 rounded-md bg-muted/30">
                  <button
                    type="button"
                    onClick={() => setMessageTag('none')}
                    className={cn('px-2.5 py-1 rounded text-[10px] font-medium transition-all', messageTag !== 'UTILITY' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground')}
                  >
                    {t('Normal')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMessageTag('UTILITY')}
                    className={cn('px-2.5 py-1 rounded text-[10px] font-medium transition-all', messageTag === 'UTILITY' ? 'bg-primary/15 text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground')}
                  >
                    {t('Maximo')}
                  </button>
                </div>

                {activeTab !== 'escrever' && (
                <Button
                  onClick={handleSend}
                  disabled={!canSend || activeTab === 'historico'}
                  className="gap-2 rounded-xl shadow-sm"
                  size="sm"
                >
                  <><Send className="h-4 w-4" /> {t('Disparar')}</>
                </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ====== PRESET EDITOR MODAL ====== */}
      <PresetEditorModal
        open={presetModalOpen}
        onOpenChange={setPresetModalOpen}
        preset={editingPreset}
        t={t}
        language={language}
      />

      {/* ====== CAMPAIGN MODAL ====== */}
      <Dialog open={campaignModalOpen} onOpenChange={setCampaignModalOpen}>
        <DialogContent className="!w-[96vw] !max-w-[96vw] h-[94vh] !max-h-[94vh] overflow-hidden flex flex-col !p-0 !gap-0 !rounded-2xl">
          <DialogHeader className="px-8 pt-5 pb-4 border-b border-border/20 shrink-0">
            <DialogTitle className="text-base">{editingCampaign ? t('Editar campanha') : t('Nova campanha')}</DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-hidden">
            <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] h-full">

              {/* ---- Coluna esquerda: Editor ---- */}
              <div className="p-8 space-y-6 overflow-y-auto">

                {/* ═══ SEÇÃO: Identidade ═══ */}
                <div className="rounded-xl border border-border/20 bg-muted/5 p-4 space-y-3">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Identidade')}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">{t('Nome')}</Label>
                      <Input
                        value={cName}
                        onChange={e => setCName(e.target.value)}
                        placeholder={t('Ex: WhatsApp Principal')}
                        className="h-9 text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">{t('Link WhatsApp')}</Label>
                      <Input
                        value={cLink}
                        onChange={e => setCLink(e.target.value)}
                        placeholder="https://wa.me/5511999..."
                        className="h-9 text-sm font-mono"
                      />
                    </div>
                  </div>
                </div>

                {/* ═══ SEÇÃO: Conteúdo ═══ */}
                <div className="rounded-xl border border-border/20 bg-muted/5 p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Conteúdo')}</p>
                    <div className="flex items-center gap-2">
                      {cPresetIds.length > 0 && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                          {cPresetIds.length} preset{cPresetIds.length > 1 ? 's' : ''}
                        </span>
                      )}
                      {(() => {
                        const sCount = cSubtitlesText.split('\n').filter((s: string) => s.trim()).length
                        if (sCount > 0) return <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 font-medium">Card Mode</span>
                        return <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">Texto</span>
                      })()}
                    </div>
                  </div>

                  {/* Presets existentes */}
                  {presets && presets.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">{t('Usar presets existentes')}</Label>
                        {cPresetIds.length > 0 && (
                          <button type="button" onClick={() => setCPresetIds([])} className="text-[10px] text-muted-foreground hover:text-foreground">
                            {t('Limpar')}
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5 max-h-[120px] overflow-y-auto p-1">
                        {presets.map(p => {
                          const selected = cPresetIds.includes(p.id)
                          return (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => {
                                setCPresetIds(prev =>
                                  selected ? prev.filter(id => id !== p.id) : [...prev, p.id]
                                )
                              }}
                              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs transition-all ${
                                selected
                                  ? 'border-primary bg-primary/10 text-primary font-medium'
                                  : 'border-border/30 bg-background hover:border-border text-muted-foreground'
                              }`}
                            >
                              <span>{p.emoji || '📋'}</span>
                              <span className="max-w-[120px] truncate">{p.name}</span>
                              {p.mode === 'card' && <span className="text-[9px] bg-emerald-500/10 text-emerald-500 px-1 rounded">card</span>}
                            </button>
                          )
                        })}
                      </div>
                      {cPresetIds.length > 0 && (
                        <p className="text-[10px] text-muted-foreground">
                          {cPresetIds.length} preset{cPresetIds.length > 1 ? 's' : ''} + {cPhrasesText.split('\n').filter((s: string) => s.trim()).length} frases manuais = {cPresetIds.length + cPhrasesText.split('\n').filter((s: string) => s.trim()).length} slots na rotação
                        </p>
                      )}
                    </div>
                  )}

                  {/* Títulos + Subtítulos lado a lado */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">{t('Títulos')}</Label>
                        <span className="text-[10px] text-muted-foreground font-mono">
                          {cPhrasesText.split('\n').filter((s: string) => s.trim()).length}
                        </span>
                      </div>
                      <Textarea
                        value={cPhrasesText}
                        onChange={e => setCPhrasesText(e.target.value)}
                        placeholder={t('Um por linha:\nOferta imperdível!\nÚltimas vagas!\nNão perca...')}
                        className="text-sm min-h-[160px] font-mono resize-y text-[12px] leading-relaxed"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">{t('Subtítulos')} <span className="text-muted-foreground font-normal text-[10px]">({t('opcional')})</span></Label>
                        <span className="text-[10px] text-muted-foreground font-mono">
                          {cSubtitlesText.split('\n').filter((s: string) => s.trim()).length}
                        </span>
                      </div>
                      <Textarea
                        value={cSubtitlesText}
                        onChange={e => setCSubtitlesText(e.target.value)}
                        placeholder={t('Pareado com títulos:\nAproveite agora\nGaranta sua vaga\nClique e saiba mais')}
                        className="text-sm min-h-[160px] font-mono resize-y text-[12px] leading-relaxed"
                      />
                    </div>
                  </div>
                  {(() => {
                    const tCount = cPhrasesText.split('\n').filter((s: string) => s.trim()).length
                    const sCount = cSubtitlesText.split('\n').filter((s: string) => s.trim()).length
                    if (sCount > 0 && sCount !== tCount) {
                      return (
                        <p className="text-[10px] text-amber-500 font-medium px-1">
                          {t(`⚠ ${tCount} títulos × ${sCount} subtítulos — devem ser iguais`)}
                        </p>
                      )
                    }
                    return null
                  })()}

                  {/* Imagens + Botão CTA lado a lado */}
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 pt-2 border-t border-border/10">
                    {/* Imagens */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="text-xs">{t('Imagens')}</Label>
                        {cImages.filter(Boolean).length > 0 && (
                          <span className="text-[10px] text-primary font-medium">{cImages.filter(Boolean).length}</span>
                        )}
                        <span className="text-[10px] text-muted-foreground">({t('opcional')})</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <label
                          htmlFor="c-img-upload-new"
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-border/40 text-[11px] text-muted-foreground cursor-pointer hover:border-primary/40 hover:text-primary transition-all"
                        >
                          <ImagePlus className="h-3.5 w-3.5" /> {t('Upload')}
                        </label>
                        <button
                          type="button"
                          onClick={openMediaPicker}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-border/40 text-[11px] text-muted-foreground hover:border-primary/40 hover:text-primary transition-all"
                        >
                          <Layers className="h-3.5 w-3.5" /> {t('Mídia')}
                        </button>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        id="c-img-upload-new"
                        onChange={async e => {
                          const files = Array.from(e.target.files || [])
                          if (!files.length) return
                          const startIdx = cImages.length
                          setCImages(prev => [...prev, ...files.map(() => '')])
                          for (let i = 0; i < files.length; i++) {
                            await handleCImageUpload(files[i], startIdx + i)
                          }
                          e.target.value = ''
                        }}
                      />
                      {cImages.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {cImages.map((img, idx) => (
                            <div key={idx} className="relative group w-14 h-14 rounded-lg overflow-hidden border border-border/30 bg-muted/30 shrink-0">
                              {cUploadingIdx === idx ? (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                </div>
                              ) : img ? (
                                <img src={img} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <ImagePlus className="h-4 w-4 text-muted-foreground/40" />
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => setCImages(prev => prev.filter((_, i) => i !== idx))}
                                className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="h-2.5 w-2.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Botão CTA */}
                    <div className="space-y-2 min-w-[200px]">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">{t('Texto do botão')}</Label>
                        <button
                          type="button"
                          onClick={() => setCButtonTexts(prev => [...prev, ''])}
                          className="text-[10px] text-muted-foreground hover:text-primary transition-colors"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      <div className="space-y-1.5">
                        {cButtonTexts.map((txt, idx) => (
                          <div key={idx} className="flex items-center gap-1.5">
                            <Input
                              value={txt}
                              onChange={e => setCButtonTexts(prev => prev.map((v, i) => i === idx ? e.target.value : v))}
                              placeholder={t('Me chama no Zap!')}
                              className="h-8 text-xs flex-1"
                            />
                            {cButtonTexts.length > 1 && (
                              <button
                                type="button"
                                onClick={() => setCButtonTexts(prev => prev.filter((_, i) => i !== idx))}
                                className="p-1 text-muted-foreground hover:text-destructive transition-colors"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ═══ SEÇÃO: Agendamento ═══ */}
                <div className="rounded-xl border border-border/20 bg-muted/5 p-4 space-y-3">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Agendamento')}</p>

                  {/* Dias da semana */}
                  <div className="flex items-center gap-3">
                    <Label className="text-xs shrink-0">{t('Dias')}</Label>
                    <div className="flex gap-1">
                      {['D','S','T','Q','Q','S','S'].map((label, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setCDays(prev => prev.includes(idx) ? prev.filter(d => d !== idx) : [...prev, idx])}
                          className={cn(
                            'w-8 h-8 rounded-lg text-xs font-semibold transition-all',
                            cDays.includes(idx)
                              ? 'bg-primary text-primary-foreground shadow-sm'
                              : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                          )}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Horários */}
                  <div className="space-y-2 pt-2 border-t border-border/10">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">{t('Horários')}</Label>
                      <div className="flex rounded-md border border-border/30 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setCTimeMode('auto')}
                          className={cn(
                            'px-2.5 py-1 text-[10px] font-medium transition-colors',
                            cTimeMode === 'auto' ? 'bg-primary text-primary-foreground' : 'bg-muted/30 text-muted-foreground hover:text-foreground'
                          )}
                        >
                          {t('Auto')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setCTimeMode('manual')}
                          className={cn(
                            'px-2.5 py-1 text-[10px] font-medium transition-colors',
                            cTimeMode === 'manual' ? 'bg-primary text-primary-foreground' : 'bg-muted/30 text-muted-foreground hover:text-foreground'
                          )}
                        >
                          {t('Manual')}
                        </button>
                      </div>
                    </div>

                    {cTimeMode === 'auto' ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="flex items-center gap-1">
                            <label className="text-[10px] text-muted-foreground">{t('Início')}</label>
                            <input type="time" value={genStart} onChange={e => setGenStart(e.target.value)}
                              className="h-7 px-2 text-xs bg-background border border-border/40 rounded" />
                          </div>
                          <div className="flex items-center gap-1">
                            <label className="text-[10px] text-muted-foreground">{t('A cada')}</label>
                            <input type="number" value={genInterval} onChange={e => setGenInterval(e.target.value)}
                              className="h-7 w-16 px-2 text-xs bg-background border border-border/40 rounded" min="1" />
                            <span className="text-[10px] text-muted-foreground">min</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <label className="text-[10px] text-muted-foreground">{t('×')}</label>
                            <input type="number" value={genCount} onChange={e => setGenCount(e.target.value)}
                              className="h-7 w-12 px-2 text-xs bg-background border border-border/40 rounded" min="1" max="48" />
                          </div>
                          <button
                            type="button"
                            onClick={generateTimes}
                            className="h-7 px-3 text-[11px] rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium"
                          >
                            {t('Gerar')}
                          </button>
                        </div>
                        {cTimes.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {[...cTimes].sort().map((time, idx) => (
                              <span key={idx} className="px-2 py-0.5 rounded bg-muted/60 text-[10px] font-mono text-foreground/70">
                                {time}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                        {cTimes.map((time, idx) => ({ time, idx })).sort((a, b) => a.time.localeCompare(b.time)).map(({ time, idx }) => (
                          <div key={idx} className="flex items-center gap-1">
                            <input
                              type="time"
                              value={time}
                              onChange={e => setCTimes(prev => prev.map((t, i) => i === idx ? e.target.value : t))}
                              className="h-7 px-2 text-xs bg-background border border-border/40 rounded font-mono"
                            />
                            <button type="button" onClick={() => setCTimes(prev => prev.filter((_, i) => i !== idx))}
                              className="text-muted-foreground hover:text-destructive transition-colors">
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => setCTimes(prev => [...prev, '10:00'])}
                          className="h-7 px-2.5 text-[10px] text-muted-foreground hover:text-primary border border-dashed border-border/40 rounded transition-all"
                        >
                          + {t('horário')}
                        </button>
                      </div>
                    )}
                    <p className="text-[10px] text-muted-foreground">{cTimes.length} {t('horários')} · {cDays.length} {t('dias/semana')}</p>
                  </div>
                </div>

                {/* ═══ SEÇÃO: Páginas ═══ */}
                <div className="rounded-xl border border-border/20 bg-muted/5 p-4 space-y-2">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Páginas')}</p>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="c-all-pages"
                      checked={cAllAccounts}
                      onCheckedChange={(v) => { setCAllAccounts(!!v); if (v) setCAccountIds(null) }}
                    />
                    <label htmlFor="c-all-pages" className="text-xs cursor-pointer">{t('Todas as páginas')}</label>
                  </div>
                  {!cAllAccounts && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {config?.accounts.map(acc => (
                        <div key={acc.igUserId} className="flex items-center gap-1.5">
                          <Checkbox
                            id={`c-acc-${acc.igUserId}`}
                            checked={(cAccountIds ?? []).includes(acc.igUserId)}
                            onCheckedChange={(v) => {
                              setCAccountIds(prev => {
                                const arr = prev ?? []
                                return v ? [...arr, acc.igUserId] : arr.filter(x => x !== acc.igUserId)
                              })
                            }}
                          />
                          <label htmlFor={`c-acc-${acc.igUserId}`} className="text-[11px] cursor-pointer">{acc.name}</label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </div>

              {/* ---- Coluna direita: Preview ---- */}
              <div className="hidden xl:flex flex-col border-l border-border/20 bg-muted/5 p-6 overflow-y-auto">
                <div className="sticky top-0 space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold">{t('Preview')}</p>
                    <button
                      type="button"
                      onClick={() => {
                        const titles = cPhrasesText.split('\n').filter((s: string) => s.trim())
                        if (titles.length === 0) return
                        setCPreviewIdx(Math.floor(Math.random() * titles.length))
                      }}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                    >
                      <Shuffle className="h-3 w-3" /> {t('Aleatório')}
                    </button>
                  </div>

                  {/* Card mockup */}
                  {(() => {
                    const titles = cPhrasesText.split('\n').filter((s: string) => s.trim())
                    const subtitles = cSubtitlesText.split('\n').filter((s: string) => s.trim())
                    const images = cImages.filter(Boolean)
                    const btnTexts = cButtonTexts.filter((s: string) => s.trim())
                    const isCard = subtitles.length > 0
                    const idx = cPreviewIdx !== null && cPreviewIdx < titles.length ? cPreviewIdx : 0
                    const imgIdx = images.length > 0 ? (idx % images.length) : -1
                    const btnIdx = btnTexts.length > 0 ? (idx % btnTexts.length) : -1

                    if (titles.length === 0) {
                      return (
                        <div className="rounded-xl border border-border/20 bg-background p-8 flex items-center justify-center min-h-[180px]">
                          <p className="text-xs text-muted-foreground text-center">{t('Adicione títulos\npara ver o preview')}</p>
                        </div>
                      )
                    }

                    return (
                      <div className="rounded-xl border border-border/30 bg-background overflow-hidden shadow-md">
                        {imgIdx >= 0 ? (
                          <div className="w-full aspect-[1.91/1] bg-muted/30 overflow-hidden">
                            <img src={images[imgIdx]} alt="" className="w-full h-full object-cover" />
                          </div>
                        ) : (
                          <div className="w-full aspect-[1.91/1] bg-gradient-to-br from-muted/40 to-muted/20 flex items-center justify-center">
                            <ImagePlus className="h-8 w-8 text-muted-foreground/20" />
                          </div>
                        )}
                        <div className="p-3.5 space-y-1">
                          <p className="text-sm font-semibold leading-tight line-clamp-2">{titles[idx]}</p>
                          {isCard && subtitles[idx] && (
                            <p className="text-xs text-muted-foreground leading-snug line-clamp-2">{subtitles[idx]}</p>
                          )}
                        </div>
                        {btnIdx >= 0 && (
                          <div className="border-t border-border/20 p-2.5">
                            <div className="w-full py-2 rounded-lg bg-primary/10 text-primary text-xs font-semibold text-center">
                              {btnTexts[btnIdx]}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })()}

                  {/* Contadores */}
                  {(() => {
                    const titles = cPhrasesText.split('\n').filter((s: string) => s.trim())
                    const subtitles = cSubtitlesText.split('\n').filter((s: string) => s.trim())
                    const images = cImages.filter(Boolean)
                    const idx = cPreviewIdx !== null && cPreviewIdx < titles.length ? cPreviewIdx : 0
                    return (
                      <div className="space-y-2 pt-2">
                        {cPreviewIdx !== null && titles.length > 0 && (
                          <p className="text-[10px] text-muted-foreground text-center font-mono">
                            #{idx + 1}/{titles.length}
                            {images.length > 0 && ` · img ${(idx % images.length) + 1}/${images.length}`}
                          </p>
                        )}
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div className="rounded-lg bg-background border border-border/20 py-2">
                            <p className="text-sm font-bold">{titles.length}</p>
                            <p className="text-[9px] text-muted-foreground">{t('títulos')}</p>
                          </div>
                          <div className="rounded-lg bg-background border border-border/20 py-2">
                            <p className="text-sm font-bold">{subtitles.length}</p>
                            <p className="text-[9px] text-muted-foreground">{t('subtítulos')}</p>
                          </div>
                          <div className="rounded-lg bg-background border border-border/20 py-2">
                            <p className="text-sm font-bold">{images.length}</p>
                            <p className="text-[9px] text-muted-foreground">{t('imagens')}</p>
                          </div>
                        </div>
                        {subtitles.length > 0 && (
                          <div className="text-center">
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-500 text-[10px] font-semibold">
                              <CheckCircle className="h-3 w-3" /> Card Mode
                            </span>
                          </div>
                        )}
                      </div>
                    )
                  })()}
                </div>
              </div>

            </div>
          </div>

          {/* Footer fixo */}
          <div className="px-8 py-4 border-t border-border/20 bg-card/50 shrink-0 flex items-center justify-end gap-3">
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('Cancelar')}</Button>
            </DialogClose>
            <Button
              size="sm"
              onClick={saveCampaign}
              disabled={createCampaign.isPending || updateCampaign.isPending}
              className="gap-1.5"
            >
              {(createCampaign.isPending || updateCampaign.isPending) && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {t('Salvar campanha')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ====== MEDIA PICKER MODAL ====== */}
      <Dialog open={mediaPickerOpen} onOpenChange={setMediaPickerOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col gap-0 p-0">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-border/20 shrink-0">
            <DialogTitle className="text-sm">{t('Selecionar da Mídia')}</DialogTitle>
            {mediaPickerSelected.length > 0 && (
              <p className="text-[11px] text-primary mt-0.5">{mediaPickerSelected.length} {t('selecionadas')}</p>
            )}
          </DialogHeader>

          {/* Pastas */}
          {!mediaPickerLoading && mediaFolders.length > 0 && (
            <div className="flex gap-1.5 px-5 py-2.5 overflow-x-auto shrink-0 border-b border-border/20">
              {mediaFolders.map(f => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setMediaPickerFolder(f.id)}
                  className={cn(
                    'px-2.5 py-1 rounded-lg text-[11px] font-medium whitespace-nowrap transition-all shrink-0',
                    mediaPickerFolder === f.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                  )}
                >
                  {f.name} <span className="opacity-60 ml-0.5">({f.images.length})</span>
                </button>
              ))}
            </div>
          )}

          {/* Grid de imagens */}
          <div className="flex-1 overflow-y-auto p-4">
            {mediaPickerLoading ? (
              <div className="grid grid-cols-5 gap-2">
                {[...Array(15)].map((_, i) => <Skeleton key={i} className="aspect-square rounded-xl" />)}
              </div>
            ) : (() => {
              const folder = mediaFolders.find(f => f.id === mediaPickerFolder)
              const imgs = folder?.images.filter(img => !img.url.match(/\.(mp4|mov|webm)$/i)) || []
              if (imgs.length === 0) return (
                <div className="flex items-center justify-center h-32">
                  <p className="text-sm text-muted-foreground">{t('Nenhuma imagem nesta pasta')}</p>
                </div>
              )
              return (
                <div className="grid grid-cols-5 gap-2">
                  {imgs.map(img => {
                    const selected = mediaPickerSelected.includes(img.url)
                    return (
                      <button
                        key={img.id}
                        type="button"
                        onClick={() => toggleMediaImg(img.url)}
                        className={cn(
                          'relative aspect-square rounded-xl overflow-hidden border-2 transition-all',
                          selected ? 'border-primary' : 'border-transparent hover:border-border/60'
                        )}
                      >
                        <img src={img.url} alt="" className="w-full h-full object-cover" />
                        {selected && (
                          <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                            <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                              <CheckCircle className="h-4 w-4 text-primary-foreground" />
                            </div>
                          </div>
                        )}
                      </button>
                    )
                  })}
                </div>
              )
            })()}
          </div>

          <div className="px-5 py-3 border-t border-border/20 flex items-center justify-between shrink-0">
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('Cancelar')}</Button>
            </DialogClose>
            <Button size="sm" onClick={confirmMediaPicker} disabled={mediaPickerSelected.length === 0}>
              {t('Adicionar')} {mediaPickerSelected.length > 0 ? `(${mediaPickerSelected.length})` : ''}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/* ========================================================================
   PRESET CARD — grid item in the presets tab
   ======================================================================== */

function PresetCard({
  preset, isSelected, onClick, onEdit, t, isGlobal,
}: {
  preset: BroadcastPreset
  isSelected: boolean
  onClick: () => void
  onEdit: () => void
  t: (s: string) => string
  isGlobal?: boolean
}) {
  const previewText = preset.mode === 'card'
    ? preset.cardTitle || ''
    : preset.message || ''

  return (
    <div
      className={cn(
        'group relative rounded-xl border p-3.5 cursor-pointer transition-all duration-200',
        isSelected
          ? 'border-primary/40 bg-primary/[0.04]'
          : 'border-border/30 bg-card/50 hover:border-border/60 hover:bg-card/80'
      )}
      onClick={onClick}
    >
      {/* Edit button (hover) */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onEdit() }}
        className="absolute top-2.5 right-2.5 flex h-6 w-6 items-center justify-center rounded-md bg-muted/40 text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-primary/10 hover:text-primary transition-all"
        title={t('Editar')}
      >
        <Pencil className="h-3 w-3" />
      </button>

      {/* Emoji */}
      <div className="text-lg mb-2">{preset.emoji}</div>

      {/* Name */}
      <div className="text-xs font-semibold text-foreground mb-1 truncate pr-6">{preset.name}</div>

      {/* Preview (2 lines clamp) */}
      <div className="text-[10px] text-muted-foreground leading-relaxed line-clamp-2">
        {previewText}
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-1 mt-2.5">
        {preset.reach === 'max' && (
          <span className="px-1.5 py-0.5 rounded text-[8px] font-medium bg-primary/10 text-primary/70">
            {t('Maximo')}
          </span>
        )}
        {preset.imageUrl && (
          <span className="px-1.5 py-0.5 rounded text-[8px] font-medium bg-muted/30 text-muted-foreground/70">
            {t('Imagem')}
          </span>
        )}
        {preset.buttons && preset.buttons.length > 0 && (
          <span className="px-1.5 py-0.5 rounded text-[8px] font-medium bg-muted/30 text-muted-foreground/70">
            {preset.buttons.length} {t('botao')}
          </span>
        )}
        {preset.mode === 'card' && (
          <span className="px-1.5 py-0.5 rounded text-[8px] font-medium bg-muted/30 text-muted-foreground/70">
            Card
          </span>
        )}
        {isGlobal && (
          <span className="px-1.5 py-0.5 rounded text-[8px] font-medium bg-purple-500/8 text-purple-400/60">
            {t('Global')}
          </span>
        )}
      </div>
    </div>
  )
}
